/**
 * Custom Quote (RFQ) System
 * Manages a client-side quote builder stored in localStorage.
 * Allows customers to add products to a quote, review, and submit for pricing.
 */

/**
 * Announce a message to screen readers via a shared aria-live region.
 */
function announceToScreenReader(message) {
  let region = document.getElementById('quote-sr-announcer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'quote-sr-announcer';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
  }
  region.textContent = '';
  setTimeout(() => { region.textContent = message; }, 100);
}

/**
 * Genie close effect — shrink + fly drawer toward a target icon, then clean up.
 */
function genieClose(drawer, targetIcon, callback) {
  if (!targetIcon || !drawer.hasAttribute('open')) {
    SideDrawer.prototype.close.call(drawer, callback);
    return;
  }

  const iconRect = targetIcon.getBoundingClientRect();
  const drawerRect = drawer.getBoundingClientRect();

  // Calculate how far to move the drawer center to land on the icon
  const tx = (iconRect.left + iconRect.width / 2) - (drawerRect.left + drawerRect.width / 2);
  const ty = (iconRect.top + iconRect.height / 2) - (drawerRect.top + drawerRect.height / 2);

  drawer.style.setProperty('--genie-tx', `${tx}px`);
  drawer.style.setProperty('--genie-ty', `${ty}px`);

  drawer.dispatchEvent(new CustomEvent(`on:${drawer.dataset.name}:before-close`, { bubbles: true }));

  drawer.setAttribute('aria-hidden', 'true');
  drawer.classList.add('is-genie-closing');
  drawer.overlay.classList.remove('is-visible');

  removeTrapFocus(drawer.opener);
  drawer.removeEventListener('click', drawer.clickHandler);
  drawer.removeEventListener('keyup', drawer.keyupHandler);
  drawer.overlay.removeEventListener('click', drawer.clickHandler);

  setTimeout(() => {
    // Kill default transition so removing [open] doesn't cause a slide-back
    drawer.style.transition = 'none';
    drawer.classList.remove('is-genie-closing');
    drawer.removeAttribute('open');
    drawer.style.removeProperty('--genie-tx');
    drawer.style.removeProperty('--genie-ty');

    // Restore default transition on next frame so future opens animate normally
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        drawer.style.removeProperty('transition');
      });
    });

    targetIcon.classList.add('is-genie-shaking');
    targetIcon.addEventListener('animationend', () => {
      targetIcon.classList.remove('is-genie-shaking');
    }, { once: true });

    if (callback) callback();
    drawer.dispatchEvent(new CustomEvent(`on:${drawer.dataset.name}:after-close`, { bubbles: true }));
  }, 530);
}

const QuoteManager = (() => {
  const STORAGE_KEY = 'custom_quote';

  /**
   * Normalize optionValues to the canonical array-of-{name, value} shape.
   * Some older items in localStorage and one Liquid-emitted button dataset use
   * a "name::value||name::value" string; everything else writes the array directly.
   * Idempotent — arrays pass through unchanged.
   */
  function normalizeOptionValues(val) {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val) {
      return val.split('||').map(pair => {
        const [name = '', value = ''] = pair.split('::');
        return { name, value };
      });
    }
    return [];
  }

  /* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — line identity.

     A quote line was keyed by variantId alone. Frame and Pad Color are real Shopify options, so
     two differently-coloured 1120s are two variantIds and stayed two lines — but Metal Shroud
     Color is a line-item PROPERTY, so both machines shared a variantId, collapsed into one line,
     and `existing.properties = item.properties` handed BOTH of them the second one's colour.
     Reported by Ryan: "it just layers them both on top of each other."

     A real cart does not have this bug because Shopify keys a cart line by variant AND
     properties. This restores that rule.

     ⚠️ IDENTITY IS OPT-IN, and that is not timidity. The ECORE roll calculator DEPENDS on the
     old behaviour: re-running it for the same roll must overwrite Room Dimensions / Roll Layout
     on the existing line, not open a second one (see addItem's "latest calculator submission
     wins"). Those properties RESTATE a line; configuration properties DEFINE one. Only the
     caller knows which it is holding, so the caller declares it via identityFromProperties, and
     every path that does not — the calculator, the sample picker — behaves exactly as before. */
  function lineKeyFor(variantId, properties) {
    const props = (properties && typeof properties === 'object') ? properties : {};
    // Sorted so key order in the bag cannot fork two otherwise identical configurations.
    const sig = Object.keys(props)
      .filter((k) => String(props[k] == null ? '' : props[k]).trim())
      .sort()
      .map((k) => k + '=' + String(props[k]).trim())
      .join('');
    return String(Number(variantId)) + (sig ? '|' + sig : '');
  }

  /* Resolve an item by lineId, falling back to variantId.

     The fallback is the safety net for this refactor: any call site still passing a variantId
     (and any row rendered before this shipped) keeps working and simply resolves to the first
     line for that variant — which is exactly today's behaviour, not a break. */
  function findItem(items, key) {
    if (key == null) return -1;
    const k = String(key);
    const byLine = items.findIndex((i) => String(i.lineId) === k);
    if (byLine !== -1) return byLine;
    const n = Number(key);
    return Number.isNaN(n) ? -1 : items.findIndex((i) => Number(i.variantId) === n);
  }

  /**
   * Load quote data from localStorage.
   * @returns {{ items: Array, updatedAt: string }}
   */
  function load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data && Array.isArray(data.items)) {
          data.items.forEach(item => {
            item.optionValues = normalizeOptionValues(item.optionValues);
            // Migrate quotes saved before line identity existed. Derived rather than random so
            // a legacy line keeps the same id on every load, and so it matches what addItem
            // would compute for the same configuration.
            if (!item.lineId) item.lineId = lineKeyFor(item.variantId, item.properties);
          });
          return data;
        }
      }
    } catch (e) {
      console.warn('[QuoteManager] Failed to load quote data:', e);
    }
    return { items: [], updatedAt: new Date().toISOString() };
  }

  /**
   * Save quote data to localStorage.
   * @param {object} data - Quote data object.
   */
  function save(data) {
    data.updatedAt = new Date().toISOString();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('[QuoteManager] Failed to save quote data:', e);
    }
  }

  /**
   * Dispatch a custom event on the document.
   * @param {string} name - Event name.
   * @param {object} detail - Event detail payload.
   */
  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  /**
   * Add an item to the quote. If the variant already exists, increment quantity.
   * @param {object} item - Item data.
   * @param {number|string} item.variantId
   * @param {number|string} item.productId
   * @param {string} item.title
   * @param {string} item.variantTitle
   * @param {string} item.price - Price as string (e.g., "1299.00")
   * @param {string} item.image - Image URL
   * @param {number} item.quantity
   * @param {string} item.url - Product page URL
   * @returns {{ item: object, isNew: boolean }}
   */
  function addItem(item) {
    const data = load();
    const variantId = Number(item.variantId);
    /* CUSTOM_LEGENDCOLOR_BY_CLAUDE — see lineKeyFor. With identityFromProperties the line is
       variant + configuration, so a second 1120 in a different shroud colour opens its own line
       while a second one in the SAME colour still just increments. Without it, the legacy
       variant-only match applies and a re-run calculator overwrites in place as before. */
    const lineId = item.identityFromProperties
      ? lineKeyFor(variantId, item.properties)
      : String(variantId);
    const existing = item.identityFromProperties
      ? data.items.find(i => String(i.lineId) === lineId)
      : data.items.find(i => Number(i.variantId) === variantId);

    if (existing) {
      existing.quantity += item.quantity || 1;
      existing.price = item.price;
      existing.compareAtPrice = item.compareAtPrice || existing.compareAtPrice || '';
      existing.optionValues = item.optionValues || existing.optionValues || [];
      existing.image = item.image || existing.image;
      // Latest calculator submission wins for properties (room dims, roll
      // layout, etc.) — re-running the calculator should overwrite the prior
      // measurements rather than stack onto them.
      if (item.properties && typeof item.properties === 'object') {
        existing.properties = item.properties;
      }
      save(data);
      dispatch('on:quote:change', { action: 'update', item: existing, quote: data });
      return { item: existing, isNew: false };
    }

    const newItem = {
      variantId,
      lineId,
      productId: Number(item.productId),
      title: item.title,
      variantTitle: item.variantTitle || '',
      optionValues: item.optionValues || [],
      // Free-form key/value bag for calculator-derived metadata (Room
      // Dimensions, Roll Layout, etc.). Carried through to the worker so the
      // Workstation can render these as item subtext.
      properties: (item.properties && typeof item.properties === 'object') ? item.properties : {},
      price: item.price,
      image: item.image || '',
      quantity: item.quantity || 1,
      url: item.url || '',
      addedAt: new Date().toISOString()
    };

    data.items.push(newItem);
    save(data);
    dispatch('on:quote:change', { action: 'add', item: newItem, quote: data });
    return { item: newItem, isNew: true };
  }

  /**
   * Remove an item from the quote.
   * @param {string|number} key - lineId, or a variantId for legacy callers.
   * @returns {boolean} Whether an item was removed.
   */
  function removeItem(key) {
    const data = load();
    const index = findItem(data.items, key);
    if (index === -1) return false;

    const removed = data.items.splice(index, 1)[0];
    save(data);
    dispatch('on:quote:change', { action: 'remove', item: removed, quote: data });
    return true;
  }

  /**
   * Update the quantity of an item in the quote.
   * @param {string|number} key - lineId, or a variantId for legacy callers.
   * @param {number} quantity - New quantity (must be >= 1).
   * @returns {boolean} Whether the item was found and updated.
   */
  function updateQuantity(key, quantity) {
    if (quantity < 1) return removeItem(key);

    const data = load();
    const index = findItem(data.items, key);
    if (index === -1) return false;

    const item = data.items[index];
    item.quantity = quantity;
    save(data);
    dispatch('on:quote:change', { action: 'update', item, quote: data });
    return true;
  }

  /**
   * Get all items in the quote.
   * @returns {Array}
   */
  function getItems() {
    return load().items;
  }

  /**
   * Get total number of items (sum of quantities).
   * @returns {number}
   */
  function getItemCount() {
    return load().items.reduce((sum, i) => sum + i.quantity, 0);
  }

  /**
   * Get the number of unique line items.
   * @returns {number}
   */
  function getLineCount() {
    return load().items.length;
  }

  /**
   * Get the quote subtotal (sum of price * quantity for all items).
   * @returns {number}
   */
  function getSubtotal() {
    return load().items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * i.quantity, 0);
  }

  /**
   * Check if a specific variant is already in the quote.
   * @param {number|string} variantId
   * @returns {boolean}
   */
  function hasItem(variantId) {
    const id = Number(variantId);
    return load().items.some(i => Number(i.variantId) === id);
  }

  /**
   * Clear all items from the quote.
   */
  function clear() {
    save({ items: [] });
    try { localStorage.removeItem('custom_quote_submission'); } catch (e) {}
    dispatch('on:quote:change', { action: 'clear', item: null, quote: { items: [] } });
  }

  function updateItemNote(key, note) {
    const data = load();
    const index = findItem(data.items, key);
    if (index === -1) return false;
    data.items[index].note = note;
    save(data);
    return true;
  }

  /* CUSTOM_LEGENDCOLOR_BY_CLAUDE — price and variant meta are facts about the VARIANT, not about
     one line, and two lines can now share a variant (same machine, different shroud colour). Both
     of these are driven by QuoteValidator, whose results Map is keyed by variantId
     (custom-quote.js:492) — so they are called with a variantId and must reach every line of it.
     Matching only the first would leave the second line quietly showing yesterday's price. */
  function findAllItems(items, key) {
    if (key == null) return [];
    const k = String(key);
    const byLine = items.filter((i) => String(i.lineId) === k);
    if (byLine.length) return byLine;
    const n = Number(key);
    return Number.isNaN(n) ? [] : items.filter((i) => Number(i.variantId) === n);
  }

  function updatePrice(key, newPrice) {
    const data = load();
    const matches = findAllItems(data.items, key);
    if (!matches.length) return false;
    matches.forEach((i) => { i.price = newPrice; });
    save(data);
    return true;
  }

  function updateVariantMeta(key, meta) {
    const data = load();
    const matches = findAllItems(data.items, key);
    if (!matches.length) return false;
    matches.forEach((item) => {
      if (meta.optionValues !== undefined) item.optionValues = meta.optionValues;
      if (meta.variantTitle !== undefined) item.variantTitle = meta.variantTitle;
      if (meta.compareAtPrice !== undefined) item.compareAtPrice = meta.compareAtPrice;
      if (meta.image !== undefined) item.image = meta.image;
    });
    save(data);
    return true;
  }

  function getData() {
    return load();
  }

  /* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — render an item's configured line-item
     properties as option rows.

     Shared by the drawer and the quote page so the two cannot drift; they were already two
     hand-maintained copies of the same option-row markup, which is how properties came to be
     missing from BOTH once they started arriving. Takes the caller's own escaper because the
     drawer uses a class method and the page a module function.

     @param {Object} item - quote item.
     @param {Function} esc - HTML escaper.
     @returns {string} zero or more `<div><strong>Name:</strong> Value</div>` rows.
  */
  function propertyRows(item, esc) {
    const props = (item && item.properties && typeof item.properties === 'object') ? item.properties : null;
    if (!props) return '';
    return Object.keys(props)
      .filter((k) => k && String(props[k] == null ? '' : props[k]).trim())
      .map((k) => `<div><strong>${esc(k)}:</strong> ${esc(String(props[k]).trim())}</div>`)
      .join('');
  }

  function getItemTag(item) {
    try {
      const sub = JSON.parse(localStorage.getItem('custom_quote_submission') || 'null');
      if (!sub || !sub.submitted || !item.addedAt) return null;

      const all = [];
      if (Array.isArray(sub.history)) {
        sub.history.forEach(h => all.push({ ref: h.ref, submittedAt: h.submittedAt }));
      }
      all.push({ ref: sub.refNumber, submittedAt: sub.submittedAt });

      if (item.addedAt > all[all.length - 1].submittedAt) {
        return { text: 'Recently Added', ref: null };
      }

      for (let i = 0; i < all.length; i++) {
        const prevBound = i === 0 ? null : all[i - 1].submittedAt;
        if (!prevBound || item.addedAt > prevBound) {
          if (item.addedAt <= all[i].submittedAt) {
            return { text: all[i].ref, ref: all[i].ref };
          }
        }
      }
      return null;
    } catch (e) { return null; }
  }

  return {
    addItem,
    removeItem,
    updateQuantity,
    getItems,
    getItemCount,
    getLineCount,
    getSubtotal,
    hasItem,
    clear,
    updateItemNote,
    updatePrice,
    updateVariantMeta,
    propertyRows,
    getData,
    getItemTag,
    normalizeOptionValues
  };
})();

const QuoteValidator = (() => {
  const _cache = new Map();
  const CACHE_TTL = 60000;

  function extractHandle(url) {
    if (!url) return null;
    const m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async function fetchProduct(handle) {
    const cached = _cache.get(handle);
    if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

    try {
      const res = await fetch('/products/' + handle + '.js');
      if (res.status === 404) {
        _cache.set(handle, { data: null, ts: Date.now() });
        return null;
      }
      if (!res.ok) return undefined;
      const product = await res.json();
      _cache.set(handle, { data: product, ts: Date.now() });
      return product;
    } catch (e) {
      return undefined;
    }
  }

  async function validateAll() {
    const items = QuoteManager.getItems();
    if (!items.length) return new Map();

    const handleMap = new Map();
    const noHandle = [];
    items.forEach(item => {
      const handle = extractHandle(item.url);
      if (!handle) { noHandle.push(item); return; }
      if (!handleMap.has(handle)) handleMap.set(handle, []);
      handleMap.get(handle).push(item);
    });

    const results = new Map();

    noHandle.forEach(item => {
      results.set(String(item.variantId), { status: 'unknown' });
    });

    const handles = [...handleMap.keys()];
    const productPromises = handles.map((handle, i) =>
      new Promise(resolve => setTimeout(() => resolve(fetchProduct(handle)), i * 50))
    );
    const products = await Promise.all(productPromises);

    handles.forEach((handle, i) => {
      const product = products[i];
      const groupItems = handleMap.get(handle);

      groupItems.forEach(item => {
        const vid = String(item.variantId);

        if (product === undefined) {
          results.set(vid, { status: 'unknown' });
          return;
        }
        if (product === null) {
          results.set(vid, { status: 'deleted', reason: 'Product is no longer available' });
          return;
        }

        const variant = product.variants.find(v => String(v.id) === vid);
        if (!variant) {
          results.set(vid, { status: 'variant_removed', reason: 'This variant is no longer available' });
          return;
        }

        if (!variant.available) {
          results.set(vid, { status: 'out_of_stock', reason: 'Currently out of stock' });
          return;
        }

        const currentPrice = (variant.price / 100).toFixed(2);
        const storedPrice = parseFloat(String(item.price).replace(/,/g, '')).toFixed(2);
        const priceChanged = currentPrice !== storedPrice;

        const liveOptionValues = product.options.map((opt, idx) => ({
          name: typeof opt === 'string' ? opt : opt.name,
          value: variant.options[idx] || ''
        }));
        const optionsMatch = item.optionValues &&
          item.optionValues.length === liveOptionValues.length &&
          item.optionValues.every((o, idx) =>
            o.name === liveOptionValues[idx].name && o.value === liveOptionValues[idx].value
          );
        const liveVariantTitle = variant.title || '';
        const liveCompareAt = (variant.compare_at_price && variant.compare_at_price > variant.price)
          ? (variant.compare_at_price / 100).toFixed(2) : '';
        const liveImage = (variant.featured_image && variant.featured_image.src) || '';
        const titleMatch = (item.variantTitle || '') === liveVariantTitle;
        const compareMatch = (item.compareAtPrice || '') === liveCompareAt;
        const imageMatch = !liveImage || (item.image || '').includes(liveImage.replace(/https?:/, ''));
        const metaChanged = !optionsMatch || !titleMatch || !compareMatch || !imageMatch;

        results.set(vid, {
          status: 'available',
          priceChanged,
          oldPrice: priceChanged ? storedPrice : undefined,
          newPrice: priceChanged ? currentPrice : undefined,
          metaChanged: metaChanged || undefined,
          newMeta: metaChanged ? {
            optionValues: !optionsMatch ? liveOptionValues : undefined,
            variantTitle: !titleMatch ? liveVariantTitle : undefined,
            compareAtPrice: !compareMatch ? liveCompareAt : undefined,
            image: !imageMatch ? liveImage : undefined
          } : undefined
        });
      });
    });

    return results;
  }

  function hasUnavailable(results) {
    for (const r of results.values()) {
      if (r.status === 'deleted' || r.status === 'variant_removed' || r.status === 'out_of_stock') return true;
    }
    return false;
  }

  return { extractHandle, fetchProduct, validateAll, hasUnavailable };
})();

const QUOTE_PLACEHOLDER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48' fill='none'%3E%3Crect width='48' height='48' rx='4' fill='%23e8e8e8'/%3E%3Cpath d='M18 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8 34l8-10 5 6 7-9 12 13H8z' fill='%23bbb'/%3E%3C/svg%3E";

function _formatMoney(amount) {
  const formatted = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const parts = formatted.split('.');
  return '$' + parts[0] + '<sup>' + parts[1] + '</sup>';
}

function _escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function _sizedImage(url, size) {
  if (!url) return '';
  if (url.includes('width=')) {
    return url.replace(/width=\d+/, 'width=' + parseInt(size, 10));
  }
  return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
}


/**
 * <custom-quote-button> Web Component
 * Handles the "Add to Quote" button on product pages.
 */
if (!customElements.get('custom-quote-button')) {
  class CustomQuoteButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.custom-quote-btn');
      if (!this.button) return;

      this.button.addEventListener('click', this.handleClick.bind(this));

      // Listen for variant changes to keep data attributes current
      const section = this.closest('.js-product');
      if (section) {
        section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Also listen at document level for variant changes (some themes dispatch there)
      document.addEventListener('on:variant:change', this.handleDocVariantChange.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleDocVariantChange.bind(this));
    }

    /**
     * Handle variant change events from the product section.
     */
    handleVariantChange(evt) {
      const variant = evt.detail?.variant;
      if (!variant) return;

      this.button.dataset.variantId = variant.id;
      this.button.dataset.price = variant.price ? (variant.price / 100).toFixed(2) : '0.00';
      this.button.dataset.variantTitle = variant.title || '';
      this.button.dataset.available = variant.available ? 'true' : 'false';
      this.refreshOptionValues();

      // Update image if the variant has a featured image
      if (variant.featured_image && variant.featured_image.src) {
        this.button.dataset.image = variant.featured_image.src;
      }

      // Disable button if variant is unavailable
      if (!variant.available) {
        this.button.disabled = true;
      } else {
        this.button.disabled = false;
      }
    }

    /* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — keep data-option-values current.

       It was stamped ONCE by Liquid (custom-quote-button.liquid:24) and nothing ever rewrote
       it: this button sits outside every [data-dynamic-variant-content] block, so the variant
       re-render does not replace it either. Change the frame colour, hit Request Quote, and the
       rep's Workstation showed the colours the page happened to LOAD with. variantId and
       variantTitle were being kept current three lines up, which is exactly what hid it —
       the item looked right everywhere the rep was likely to glance first.

       Read from the option selectors rather than evt.detail because we need option NAMES, not
       just values: index.js:1590 renders each pair as "<name>: <value>" for the rep's
       sub-description, and prefers optionValues over variantTitle when both are present. Both
       selector styles are handled — "buttons" renders radio inputs, "dropdown" renders a
       custom-select backed by a hidden native <select>. */
    refreshOptionValues() {
      const section = this.closest('.js-product');
      if (!section) return;
      const pairs = [];
      section.querySelectorAll('.option-selector[data-option]').forEach((sel) => {
        const name = sel.dataset.option;
        const checked = sel.querySelector('input.js-option:checked');
        const native = sel.querySelector('select.custom-select__native');
        const value = checked ? checked.value : (native ? native.value : '');
        if (name && value) pairs.push(name + '::' + value);
      });
      // Only overwrite when we actually read something. A failed read must leave Liquid's
      // original stamp standing rather than blanking the rep's only colour information.
      if (pairs.length) this.button.dataset.optionValues = pairs.join('||');
    }

    /* Harvest the product form's line-item properties — Metal Shroud Color and the other
       Legend colour surfaces, nameplate wording, peg configuration, cage height. Add to Cart
       has always carried these (the browser submits them with the form); Request Quote did
       not, so a customer who configured a machine and asked for a quote instead of buying had
       every one of those choices silently dropped.

       FormData rather than a DOM query: these controls live OUTSIDE the <form> and are bound
       to it with the form="" attribute (main-product.liquid:994-995), so form.querySelectorAll
       misses them entirely. FormData also reproduces checkbox semantics exactly as /cart/add
       would see them, which keeps the two paths honest with each other. */
    collectProperties(form) {
      const out = {};
      if (!form || typeof FormData !== 'function') return out;
      new FormData(form).forEach((value, key) => {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (!m) return;
        // The "Please select" placeholder posts U+00A0, not an empty string. trim() catches
        // it because JS WhiteSpace includes U+00A0 — a bare !value check would not.
        const v = String(value == null ? '' : value).trim();
        if (v) out[m[1]] = v;
      });
      return out;
    }
    /* END CUSTOM_LEGENDCOLOR_BY_CLAUDE */

    /**
     * Handle variant change from document-level (filter to our product).
     */
    handleDocVariantChange(evt) {
      // Only respond if we're inside the relevant section
      const variant = evt.detail?.variant;
      if (!variant) return;

      const section = this.closest('.js-product');
      if (!section) return;

      // Check if the event target is within the same section
      const eventSection = evt.target?.closest?.('.js-product');
      if (eventSection && eventSection !== section) return;

      this.handleVariantChange(evt);
    }

    /**
     * Handle Add to Quote button click.
     */
    handleClick(e) {
      e.preventDefault();

      const btn = this.button;
      if (btn.disabled || btn.classList.contains('is-loading')) return;

      // Get quantity from the product form
      const form = this.closest('product-form')?.querySelector('.js-product-form')
        || this.closest('form');
      const qtyInput = form?.querySelector('input[name="quantity"]');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      const item = {
        variantId: btn.dataset.variantId,
        productId: btn.dataset.productId,
        title: btn.dataset.title,
        variantTitle: btn.dataset.variantTitle || '',
        optionValues: QuoteManager.normalizeOptionValues(btn.dataset.optionValues),
        price: btn.dataset.price,
        image: btn.dataset.image || '',
        quantity,
        url: btn.dataset.url || ''
      };

      /* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — carry the configured options.
         Set only when something was actually harvested: addItem() REPLACES an existing item's
         properties wholesale (deliberately, so the latest calculator submission wins), so
         sending an empty bag on a re-add would wipe calculator-derived Room Dimensions / Roll
         Layout. No product can hit both paths today — the only non-Legend templates with custom
         options are ecore-sample (excluded from this button by handle) and landing-page-pavers
         (not a product) — but the guard is what keeps that true after the next template. */
      const properties = this.collectProperties(form);
      if (Object.keys(properties).length) item.properties = properties;
      /* These properties CONFIGURE the machine, so they are part of the line's identity: a second
         1120 in a different shroud colour is a second line, exactly as it would be in the cart.
         The calculator paths deliberately do not set this — theirs RESTATE a line's measurements
         and must keep overwriting in place. See lineKeyFor. */
      item.identityFromProperties = true;
      /* END CUSTOM_LEGENDCOLOR_BY_CLAUDE */

      // Validate
      if (!item.variantId || !item.productId) {
        console.warn('[QuoteButton] Missing variant or product ID');
        return;
      }

      // Add to quote
      const result = QuoteManager.addItem(item);

      // Visual feedback then open drawer or show notification
      this.showFeedback(result.isNew);
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer) {
        if (typeof quickAddDrawer.addedToQuote === 'function') quickAddDrawer.addedToQuote();
      } else {
        setTimeout(() => {
          const drawer = document.querySelector('#quote-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }, 600);
      }
    }

    /**
     * Show spinner → checkmark feedback (same animation as Add to Cart).
     */
    showFeedback() {
      const btn = this.button;

      // Hide text, show spinner
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');

      // After a brief spinner, transition to checkmark
      setTimeout(() => {
        btn.classList.add('is-success');
      }, 300);

      // Reset after checkmark animation completes
      setTimeout(() => {
        btn.classList.remove('is-loading');
        btn.classList.remove('is-success');
        btn.removeAttribute('aria-disabled');
      }, 1700);
    }
  }

  customElements.define('custom-quote-button', CustomQuoteButton);
}


/* CUSTOM_RFQ_BY_CLAUDE (Ryan 2026-08-23) - click-and-type quantity entry.

   The quote's qty selectors rendered the number as a <span>, so +/- were the ONLY way to reach a
   quantity - fine for 2, painful for 40. The cart has always allowed typing because it uses the
   theme's <quantity-input> around a real <input type="number">. This gives the quote the same
   ability without adopting the cart's chrome, which Ryan prefers as-is.

   Commit semantics deliberately mirror the theme's QuantityInput (quantity-input.js):
     - `change` (fires on blur, and on Enter) is the commit point, NOT `input` - committing per
       keystroke would re-render the list out from under the caret, since every QuoteManager write
       dispatches on:quote:change and the drawer/page re-render wholesale from storage.
     - Enter blurs to force that commit; Escape abandons the edit.
     - Focus selects the existing value on a fine pointer, so typing replaces rather than appends.
     - Blank or non-numeric reverts by re-rendering from QuoteManager, the only source of truth
       here - there is no trustworthy "previous value" left in the DOM after a re-render.
     - < 1 falls through to QuoteManager.updateQuantity, which removes the line. Same as the cart,
       where typing 0 removes it.
   Returns its own unbind so disconnectedCallback stays symmetrical. */
function bindTypedQty(root, valueClass, rowClass, revert, isBlocked) {
  const inputFrom = (evt) => (evt.target.closest ? evt.target.closest('.' + valueClass) : null);

  const commit = (input) => {
    const row = input.closest('.' + rowClass);
    if (!row) return;
    if (isBlocked && isBlocked(row)) { revert(); return; }

    const raw = String(input.value).trim();
    const qty = parseInt(raw, 10);
    if (raw === '' || Number.isNaN(qty)) { revert(); return; }

    QuoteManager.updateQuantity(row.dataset.lineId || row.dataset.variantId, qty);
  };

  const onChange = (evt) => { const i = inputFrom(evt); if (i) commit(i); };
  const onKeydown = (evt) => {
    const i = inputFrom(evt);
    if (!i) return;
    if (evt.key === 'Enter') { evt.preventDefault(); i.blur(); }
    else if (evt.key === 'Escape') { evt.preventDefault(); revert(); }
  };
  const onFocusin = (evt) => {
    const i = inputFrom(evt);
    if (i && window.matchMedia('(pointer: fine)').matches) i.select();
  };

  root.addEventListener('change', onChange);
  root.addEventListener('keydown', onKeydown);
  root.addEventListener('focusin', onFocusin);

  return () => {
    root.removeEventListener('change', onChange);
    root.removeEventListener('keydown', onKeydown);
    root.removeEventListener('focusin', onFocusin);
  };
}
/* END CUSTOM_RFQ_BY_CLAUDE */


/**
 * <custom-quote-drawer> Web Component
 * Slide-out drawer for managing quote items. Extends SideDrawer for
 * open/close animations, focus trapping, Escape key, and overlay handling.
 */
if (!customElements.get('custom-quote-drawer')) {
  class CustomQuoteDrawer extends SideDrawer {
    constructor() {
      super();
      this.itemsContainer = this.querySelector('.custom-quote-drawer__items');
      this.emptyState = this.querySelector('.custom-quote-drawer__empty');
      this.footer = this.querySelector('.custom-quote-drawer__footer');
      this.countEl = this.querySelector('.custom-quote-drawer__count');
      this.subtotalEl = this.querySelector('.custom-quote-drawer__subtotal');
    }

    connectedCallback() {
      this.initHeaderIcon();
      this.renderItems();
      this.updateBadge();

      this.quoteChangeHandler = () => {
        this.renderItems();
        this.updateBadge();
      };
      document.addEventListener('on:quote:change', this.quoteChangeHandler);

      // Delegate clicks inside the drawer for qty/remove buttons
      this.delegateHandler = this.handleDelegatedClick.bind(this);
      this.itemsContainer.addEventListener('click', this.delegateHandler);

      // CUSTOM_RFQ_BY_CLAUDE - typed quantity entry
      this.unbindTypedQty = bindTypedQty(
        this.itemsContainer,
        'custom-quote-drawer__qty-value',
        'custom-quote-drawer__item',
        () => this.quoteChangeHandler()
      );
    }

    disconnectedCallback() {
      document.removeEventListener('on:quote:change', this.quoteChangeHandler);
      this.itemsContainer.removeEventListener('click', this.delegateHandler);
      if (this.unbindTypedQty) this.unbindTypedQty();
    }

    close(callback) {
      genieClose(this, document.getElementById('quote-icon'), callback);
    }

    /**
     * Bind the header quote icon to open this drawer.
     */
    initHeaderIcon() {
      const icon = document.getElementById('quote-icon');
      if (!icon) return;

      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(icon);
      });
      icon.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open(icon);
        }
      });
    }

    /**
     * Render all quote items into the drawer.
     */
    renderItems() {
      const items = QuoteManager.getItems();
      const count = QuoteManager.getItemCount();

      if (this.countEl) this.countEl.textContent = count;

      const viewQuoteLink = this.querySelector('.custom-quote-drawer__view-quote');

      if (items.length === 0) {
        this.itemsContainer.innerHTML = '';
        this.emptyState.style.display = '';
        this.footer.style.display = 'none';
        if (viewQuoteLink) viewQuoteLink.style.display = 'none';
        return;
      }

      this.emptyState.style.display = 'none';
      this.footer.style.display = '';
      if (viewQuoteLink) viewQuoteLink.style.display = '';

      const btnTextEl = this.querySelector('.custom-quote-drawer__checkout-btn');
      if (btnTextEl) {
        const label = items.length > 1 ? 'Request Bulk Pricing' : 'Request Pricing';
        const svgEl = btnTextEl.querySelector('svg');
        const svgHtml = svgEl ? svgEl.outerHTML : '';
        btnTextEl.innerHTML = svgHtml + '\n      ' + label;
      }

      const html = items.map(item => {
        const imageHtml = item.image
          ? `<div class="custom-quote-drawer__item-image"><a href="${item.url}" class="block"><img src="${this.sizedImage(item.image, '240x')}" alt="${this.escapeHtml(item.title)}" loading="lazy"></a></div>`
          : `<div class="custom-quote-drawer__item-placeholder"></div>`;

        let variantHtml = '';
        const opts = Array.isArray(item.optionValues) ? item.optionValues : [];
        let variantRows = '';
        if (opts.length && !(opts.length === 1 && opts[0].value === 'Default Title')) {
          variantRows = opts.map(o => `<div><strong>${this.escapeHtml(o.name)}:</strong> ${this.escapeHtml(o.value)}</div>`).join('');
        } else if (item.variantTitle && item.variantTitle !== 'Default Title') {
          const parts = item.variantTitle.split(' / ');
          variantRows = parts.map(p => `<div>${this.escapeHtml(p)}</div>`).join('');
        }
        /* CUSTOM_LEGENDCOLOR_BY_CLAUDE (Ryan 2026-08-12) — show the configured properties too.
           They are captured, stored, submitted and written onto the draft order, but this
           surface only ever drew optionValues, so a customer who had just picked a Metal Shroud
           Color saw no trace of it in their own quote. The ECORE calculator's Room Dimensions
           were invisible here for the same reason. Appended into the SAME wrapper as the
           variant options so the two read as one list rather than two stacked blocks. */
        variantRows += QuoteManager.propertyRows(item, this.escapeHtml);
        if (variantRows) {
          variantHtml = `<div class="custom-quote-drawer__variants text-sm">${variantRows}</div>`;
        }

        return `
          <div class="custom-quote-drawer__item" data-variant-id="${item.variantId}" data-line-id="${item.lineId || item.variantId}">
            ${imageHtml}
            <div class="custom-quote-drawer__item-details">
              <a href="${item.url}" class="custom-quote-drawer__item-title">${this.escapeHtml(item.title)}</a>
              ${variantHtml}
            </div>
            <div class="custom-quote-drawer__item-qty">
              <div class="custom-quote-drawer__qty-wrap">
                <button type="button" class="custom-quote-drawer__qty-btn" data-action="decrease" aria-label="Decrease quantity">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><line x1="2" y1="6" x2="10" y2="6"/></svg>
                </button>
                <input type="number" class="custom-quote-drawer__qty-value" value="${item.quantity}" min="0" step="1" inputmode="numeric" autocomplete="off" aria-label="Quantity for ${this.escapeHtml(item.title)}">
                <button type="button" class="custom-quote-drawer__qty-btn" data-action="increase" aria-label="Increase quantity">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="2" x2="6" y2="10"/><line x1="2" y1="6" x2="10" y2="6"/></svg>
                </button>
              </div>
              <button type="button" class="custom-quote-drawer__remove" data-action="remove" aria-label="Remove ${this.escapeHtml(item.title)}">
                <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon" fill="currentColor"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z"/><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z"/></svg>
              </button>
            </div>
          </div>`;
      }).join('');

      this.itemsContainer.innerHTML = html;
    }

    /**
     * Handle click events delegated from the items container.
     */
    handleDelegatedClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const row = btn.closest('.custom-quote-drawer__item');
      if (!row) return;

      const variantId = row.dataset.lineId || row.dataset.variantId;
      const action = btn.dataset.action;

      if (action === 'remove') {
        QuoteManager.removeItem(variantId);
      } else if (action === 'decrease' || action === 'increase') {
        const current = parseInt(row.querySelector('.custom-quote-drawer__qty-value').value, 10);
        const newQty = action === 'increase' ? current + 1 : current - 1;
        QuoteManager.updateQuantity(variantId, newQty);
      }
    }

    /**
     * Update the header badge with the current quote item count.
     */
    updateBadge() {
      const bubble = document.getElementById('quote-icon-bubble');
      if (!bubble) return;

      const count = QuoteManager.getItemCount();
      if (count > 0) {
        bubble.innerHTML = `<div class="custom-quote-badge absolute bg-theme-text text-theme-bg text-center">
          <span aria-hidden="true">${count}</span>
          <span class="visually-hidden">${count} item${count !== 1 ? 's' : ''} in quote</span>
        </div>`;
      } else {
        bubble.innerHTML = '';
      }
    }

    /**
     * Format a number as USD currency.
     */
    formatMoney(amount) {
      return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /**
     * Escape HTML to prevent XSS.
     */
    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /**
     * Get a sized Shopify image URL.
     */
    sizedImage(url, size) {
      if (!url) return '';
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
    }
  }

  customElements.define('custom-quote-drawer', CustomQuoteDrawer);
}


/**
 * <custom-quote-page> Web Component
 * Full-page quote review with larger images, per-item Add to Cart, and summary sidebar.
 */
if (!customElements.get('custom-quote-page')) {
  class CustomQuotePage extends HTMLElement {
    connectedCallback() {
      this.itemsContainer = this.querySelector('.custom-quote-page__items');
      this.emptyState = this.querySelector('.custom-quote-page__empty');
      this.layout = this.querySelector('.custom-quote-page__layout');
      this.headingEl = this.querySelector('.custom-quote-page__heading');
      this.dateEl = this.querySelector('.custom-quote-page__date-value');
      this.lineCountEl = this.querySelector('.custom-quote-page__line-count');
      this.itemCountEl = this.querySelector('.custom-quote-page__item-count');
      this.subtotalEl = this.querySelector('.custom-quote-page__subtotal');
      this.installationRow = this.querySelector('.custom-quote-page__installation-row');
      this.sortSelect = this.querySelector('#quote-sort');
      this.currentSort = 'added';
      this._validationResults = null;

      this.setDate();
      this.renderItems();
      this.checkSubmissionState();
      this.runValidation();

      this.quoteChangeHandler = () => {
        this.renderItems();
        if (this._validationResults) this.applyValidationUI();
      };
      document.addEventListener('on:quote:change', this.quoteChangeHandler);

      // Sort dropdown
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.currentSort = this.sortSelect.value;
          this.renderItems();
        });
      }

      this.delegateHandler = this.handleDelegatedClick.bind(this);
      this.addEventListener('click', this.delegateHandler);

      // CUSTOM_RFQ_BY_CLAUDE - typed quantity entry. Mirrors the +/- guard: an unavailable or
      // out-of-stock line refuses the edit and snaps back.
      this.unbindTypedQty = bindTypedQty(
        this,
        'custom-quote-page__qty-value',
        'custom-quote-page__item',
        () => this.quoteChangeHandler(),
        (row) => row.classList.contains('custom-quote-page__item--unavailable')
          || row.classList.contains('custom-quote-page__item--out-of-stock')
      );

      // Save per-item notes on blur
      this.addEventListener('focusout', (e) => {
        const textarea = e.target.closest('.custom-quote-page__note-input');
        if (!textarea) return;
        const row = textarea.closest('.custom-quote-page__item');
        if (!row) return;
        QuoteManager.updateItemNote(row.dataset.lineId || row.dataset.variantId, textarea.value.trim());
        const toggle = row.querySelector('.custom-quote-page__note-toggle');
        if (toggle) {
          const hasNote = !!textarea.value.trim();
          toggle.lastChild.textContent = hasNote ? ' Edit note' : ' Add a note';
          toggle.classList.toggle('has-note', hasNote);
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('on:quote:change', this.quoteChangeHandler);
      this.removeEventListener('click', this.delegateHandler);
      if (this.unbindTypedQty) this.unbindTypedQty();
    }

    renderItems() {
      let items = QuoteManager.getItems();

      if (items.length === 0) {
        this.itemsContainer.innerHTML = '';
        this.emptyState.style.display = '';
        this.layout.style.display = 'none';
        return;
      }

      // Sort items based on current selection
      if (this.currentSort === 'alpha') {
        items = items.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      }

      this.emptyState.style.display = 'none';
      this.layout.style.display = '';

      const html = items.map(item => {
        const price = parseFloat(String(item.price).replace(/,/g, '')) || 0;
        const compareAtPrice = parseFloat(String(item.compareAtPrice || '').replace(/,/g, '')) || 0;
        const lineTotal = price * item.quantity;
        const hasCompare = compareAtPrice > price;
        const tag = QuoteManager.getItemTag(item);

        const imageHtml = item.image
          ? `<div class="custom-quote-page__item-image">
               <a href="${item.url}"><img src="${_sizedImage(item.image, '400x')}" alt="${_escapeHtml(item.title)}" loading="lazy" onerror="this.onerror=null;this.src='${QUOTE_PLACEHOLDER_SVG}';this.style.objectFit='contain';"></a>
             </div>`
          : `<div class="custom-quote-page__item-image custom-quote-page__item-placeholder"></div>`;

        const priceHtml = hasCompare
          ? `<div class="custom-quote-page__price-row">
               <strong class="custom-quote-page__price">${_formatMoney(price)}</strong>
               <span class="custom-quote-page__compare-price">${_formatMoney(compareAtPrice)}</span>
             </div>`
          : `<div class="custom-quote-page__price-row">
               <strong class="custom-quote-page__price">${_formatMoney(price)}</strong>
             </div>`;

        let optionsHtml = '';
        const opts = item.optionValues || [];
        let optionRows = '';
        if (opts.length && !(opts.length === 1 && opts[0].value === 'Default Title')) {
          optionRows = opts.map(o => `<div><strong>${_escapeHtml(o.name)}:</strong> ${_escapeHtml(o.value)}</div>`).join('');
        }
        // CUSTOM_LEGENDCOLOR_BY_CLAUDE — configured properties, same treatment as the drawer.
        optionRows += QuoteManager.propertyRows(item, _escapeHtml);
        if (optionRows) {
          optionsHtml = `<div class="custom-quote-page__options text-sm">${optionRows}</div>`;
        }

        const tagCls2 = tag ? (tag.ref ? 'custom-quote-addon-tag custom-quote-addon-tag--ref' : 'custom-quote-addon-tag') : '';
        const addonTag = tag ? `<span class="${tagCls2}">${_escapeHtml(tag.text)}</span>` : '';

        const lineTotalHtml = `<div class="custom-quote-page__line-total">
          <strong>${_formatMoney(lineTotal)}</strong>
        </div>`;

        return `
          <div class="custom-quote-page__item" data-variant-id="${item.variantId}" data-line-id="${item.lineId || item.variantId}">
            ${imageHtml}
            <div class="custom-quote-page__item-details">
              <a href="${item.url}" class="custom-quote-page__item-title">${_escapeHtml(item.title)}</a>${addonTag}
              ${priceHtml}
              ${optionsHtml}
              <div class="custom-quote-page__item-controls">
                <div class="custom-quote-page__item-qty">
                  <button type="button" class="custom-quote-page__qty-btn" data-action="decrease" aria-label="Decrease quantity">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><line x1="2" y1="6" x2="10" y2="6"/></svg>
                  </button>
                  <input type="number" class="custom-quote-page__qty-value" value="${item.quantity}" min="0" step="1" inputmode="numeric" autocomplete="off" aria-label="Quantity for ${_escapeHtml(item.title)}">
                  <button type="button" class="custom-quote-page__qty-btn" data-action="increase" aria-label="Increase quantity">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="2" x2="6" y2="10"/><line x1="2" y1="6" x2="10" y2="6"/></svg>
                  </button>
                </div>
                <button type="button" class="custom-quote-page__remove" data-action="remove" aria-label="Remove ${_escapeHtml(item.title)}">
                  <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon" fill="currentColor"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z"/><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z"/></svg>
                </button>
                <button type="button" class="custom-quote-page__atc-btn btn btn--secondary" data-action="add-to-cart" data-variant-id="${item.variantId}" aria-label="Add to cart">
                  Add to Cart
                </button>
              </div>
              <div class="custom-quote-page__item-note">
                <button type="button" class="custom-quote-page__note-toggle${item.note ? ' has-note' : ''}" data-action="toggle-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  ${item.note ? ' Edit note' : ' Add a note'}
                </button>
                <textarea class="custom-quote-page__note-input input" rows="2" placeholder="Questions about this product, special requirements..."${item.note ? '' : ' style="display:none;"'}>${item.note ? _escapeHtml(item.note) : ''}</textarea>
              </div>
            </div>
            ${lineTotalHtml}
          </div>`;
      }).join('');

      this.itemsContainer.innerHTML = html;
      this.updateSummary();
    }

    setDate() {
      if (!this.dateEl) return;
      const d = new Date();
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      this.dateEl.textContent = `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }

    updateSummary() {
      const lineCount = QuoteManager.getLineCount();
      const itemCount = QuoteManager.getItemCount();
      const subtotal = QuoteManager.getSubtotal();

      if (this.lineCountEl) this.lineCountEl.textContent = lineCount;
      if (this.itemCountEl) this.itemCountEl.textContent = itemCount;
      if (this.subtotalEl) this.subtotalEl.innerHTML = _formatMoney(subtotal);

      if (this.headingEl) {
        this.headingEl.textContent = lineCount === 1 ? 'Your 1-Item Quote' : `Your ${lineCount}-Item Quote`;
      }

      /* Show installation row only for quotes over $5,000 (subtotal is in dollars) */
      if (this.installationRow) {
        this.installationRow.style.display = subtotal >= 5000 ? '' : 'none';
      }
    }

    /**
     * Check if a quote has been submitted and show the banner + customer info.
     * If quote was modified after submission, show resubmit prompt instead.
     */
    checkSubmissionState() {
      try {
        const raw = localStorage.getItem('custom_quote_submission');
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || !data.submitted) return;

        const banner = this.querySelector('.custom-quote-page__submitted-banner');
        const closeBtn = banner ? banner.querySelector('.custom-quote-page__banner-close') : null;
        const submitCta = this.querySelector('.custom-quote-page__summary .btn--primary');
        const ctaLead = this.querySelector('.custom-quote-page__cta-lead');

        if (data.modifiedAfterSubmission) {
          // Check if banner was dismissed and no new items since
          let bannerHidden = false;
          if (data.bannerDismissedAt) {
            const items = QuoteManager.getItems();
            const hasNewerItems = items.some(i => i.addedAt && i.addedAt > data.bannerDismissedAt);
            bannerHidden = !hasNewerItems;
          }

          // Modified after submission — prompt to resubmit
          if (banner) {
            banner.style.display = bannerHidden ? 'none' : '';
            banner.style.background = 'rgba(230, 150, 0, 0.08)';
            banner.style.borderColor = 'rgba(230, 150, 0, 0.25)';
            const svg = banner.querySelector('svg:first-child');
            if (svg) svg.style.color = '#c77d00';
            const titleEl = banner.querySelector('.custom-quote-page__submitted-title');
            if (titleEl) titleEl.textContent = 'List Updated';
            const bodyEl = banner.querySelector('.custom-quote-page__submitted-body');
            if (bodyEl) bodyEl.innerHTML = 'You\u2019ve added items since your last submission. Shipping &amp; installation estimates may need to be recalculated. <a href="/pages/request-a-quote-form" style="font-weight:600;text-decoration:underline;">Resubmit your request</a> to update it.';
            if (closeBtn) closeBtn.style.display = '';
          }

          // Wire up close button
          if (closeBtn && !closeBtn._bound) {
            closeBtn._bound = true;
            closeBtn.addEventListener('click', () => {
              if (banner) banner.style.display = 'none';
              try {
                const sub = JSON.parse(localStorage.getItem('custom_quote_submission') || 'null');
                if (sub) {
                  sub.bannerDismissedAt = new Date().toISOString();
                  localStorage.setItem('custom_quote_submission', JSON.stringify(sub));
                }
              } catch (e) {}
            });
          }

          if (submitCta) {
            submitCta.textContent = 'Resubmit Request';
            submitCta.setAttribute('href', '/pages/request-a-quote-form');
          }
          if (ctaLead) ctaLead.textContent = 'Your list has changed — resubmit for updated pricing';
        } else {
          // Normal submitted state — no close button
          if (banner) {
            banner.style.display = '';
            const refEl = banner.querySelector('.custom-quote-page__submitted-ref');
            if (refEl) refEl.textContent = data.refNumber;
            if (closeBtn) closeBtn.style.display = 'none';
          }

          if (submitCta) {
            submitCta.textContent = 'Request Submitted';
            submitCta.classList.remove('btn--primary');
            submitCta.classList.add('btn--secondary');
            submitCta.style.pointerEvents = 'none';
            submitCta.style.opacity = '0.7';
            submitCta.removeAttribute('href');
          }
          if (ctaLead) ctaLead.textContent = 'Our team is reviewing your request';
        }

        // Change freight/installation text when under review
        const pendingBadges = this.querySelectorAll('.custom-quote-page__pending-badge');
        pendingBadges.forEach(badge => {
          badge.textContent = data.modifiedAfterSubmission ? 'Contact us' : 'Being calculated';
        });

        // Render submission history under date bar
        this.renderSubmissionHistory(data);

        // Show customer info card (address block format)
        this.renderCustomerInfoCard(data);
      } catch (e) { /* silently fail */ }
    }

    /**
     * Render submission history entries under the date bar.
     */
    renderSubmissionHistory(data) {
      const container = this.querySelector('.custom-quote-page__submission-history');
      if (!container) return;

      // Build ordered list of all submissions with their item snapshots
      const entries = [];
      if (Array.isArray(data.history)) {
        data.history.forEach(h => entries.push({ ref: h.ref, date: h.submittedAt, items: h.items || [] }));
      }
      if (data.refNumber && data.submittedAt) {
        entries.push({ ref: data.refNumber, date: data.submittedAt, items: data.itemsSnapshot || [] });
      }

      if (!entries.length) { container.innerHTML = ''; return; }

      const formatDate = (iso) => {
        const d = new Date(iso);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
      };

      /**
       * Compute a human-readable diff between two item snapshots.
       * Returns an array of strings like "Added X", "Removed Y", "Changed Qty of Z to 5".
       */
      const diffItems = (prevItems, currItems) => {
        const changes = [];
        const prevMap = new Map(prevItems.map(i => [String(i.variantId), i]));
        const currMap = new Map(currItems.map(i => [String(i.variantId), i]));

        // Added items
        currMap.forEach((item, id) => {
          if (!prevMap.has(id)) changes.push(`Added ${item.title}`);
        });

        // Removed items
        prevMap.forEach((item, id) => {
          if (!currMap.has(id)) changes.push(`Removed ${item.title}`);
        });

        // Qty changes
        currMap.forEach((item, id) => {
          const prev = prevMap.get(id);
          if (prev && prev.quantity !== item.quantity) {
            changes.push(`Changed Qty of ${item.title} to ${item.quantity}`);
          }
        });

        return changes;
      };

      const isLast = (i) => i === entries.length - 1;

      container.innerHTML = entries.map((e, i) => {
        const label = entries.length === 1
          ? `Submitted ${formatDate(e.date)}`
          : `Submission ${i + 1} \u2014 ${formatDate(e.date)}`;

        let diffHtml = '';
        if (i === 0 && e.items.length) {
          diffHtml = `<div class="custom-quote-page__submission-detail">${e.items.length} item${e.items.length !== 1 ? 's' : ''}</div>`;
        } else if (i > 0) {
          const changes = diffItems(entries[i - 1].items, e.items);
          if (changes.length) {
            diffHtml = changes.map(c => `<div class="custom-quote-page__submission-detail">${_escapeHtml(c)}</div>`).join('');
          }
        }

        // Older entries start collapsed; most recent stays open
        const collapsed = entries.length > 1 && !isLast(i) ? ' custom-quote-page__submission-entry--collapsed' : '';
        const toggleBtn = entries.length > 1 && !isLast(i)
          ? `<button type="button" class="custom-quote-page__submission-toggle" aria-label="Toggle details" aria-expanded="false">+</button>`
          : '';

        return `<div class="custom-quote-page__submission-entry${collapsed}">
          <div><span class="custom-quote-page__submission-ref">${_escapeHtml(e.ref)}</span> ${label}${toggleBtn}</div>
          ${diffHtml}
        </div>`;
      }).join('');

      // Wire up toggle buttons — spinning +/− like Q&A accordion
      container.querySelectorAll('.custom-quote-page__submission-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
          const entry = btn.closest('.custom-quote-page__submission-entry');
          if (!entry) return;
          const isCollapsed = entry.classList.toggle('custom-quote-page__submission-entry--collapsed');
          btn.textContent = isCollapsed ? '+' : '\u2212';
          btn.setAttribute('aria-expanded', String(!isCollapsed));
          btn.classList.toggle('custom-quote-page__submission-toggle--open', !isCollapsed);
        });
      });
    }

    /**
     * Render customer info in address-block format.
     */
    renderCustomerInfoCard(data) {
      const infoCard = this.querySelector('.custom-quote-page__customer-info');
      if (!infoCard) return;

      const detailsEl = infoCard.querySelector('.custom-quote-page__customer-info-details');
      if (!detailsEl) return;

      const line = (text, muted) => {
        const cls = muted
          ? 'custom-quote-page__customer-info-line custom-quote-page__customer-info-line--muted'
          : 'custom-quote-page__customer-info-line';
        return `<span class="${cls}">${_escapeHtml(text)}</span>`;
      };

      const lines = [];

      // Organization — only if not residential
      if (data.organization && data.organization !== 'n/a - residence') {
        lines.push(line(data.organization));
      }

      // Street — show address if provided, em dash if not
      if (data.street) {
        lines.push(line(data.street));
      } else {
        lines.push(`<span class="custom-quote-page__customer-info-line">Street: <span class="custom-quote-page__customer-info-line--muted">\u2014</span></span>`);
      }

      // City, State Zip
      if (data.city && data.state && data.zip) {
        lines.push(line(data.city + ', ' + data.state + ' ' + data.zip));
      } else if (data.zip) {
        lines.push(line('Zip: ' + data.zip));
      }

      // Country — assumed US if zip validated
      if (data.city && data.state) {
        lines.push(line('United States'));
      }

      // Attn: Name
      if (data.name) lines.push(line('Attn: ' + data.name));

      // Phone
      if (data.phone) lines.push(line(data.phone));

      // Email
      if (data.email) lines.push(line(data.email));

      if (lines.length) {
        detailsEl.innerHTML = lines.join('');
        infoCard.style.display = '';
      }
    }

    handleDelegatedClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const action = btn.dataset.action;

      if (action === 'clear-quote') {
        this.handleClear();
        return;
      }

      if (action === 'add-all-to-cart') {
        this.addAllToCart(btn);
        return;
      }

      if (action === 'print-quote') {
        const origTitle = document.title;
        document.title = ' ';
        window.print();
        document.title = origTitle;
        return;
      }

      if (action === 'toggle-note') {
        const noteSection = btn.closest('.custom-quote-page__item-note');
        const textarea = noteSection.querySelector('.custom-quote-page__note-input');
        const isHidden = textarea.style.display === 'none';
        textarea.style.display = isHidden ? '' : 'none';
        if (isHidden) textarea.focus();
        return;
      }

      const row = btn.closest('.custom-quote-page__item');
      if (!row) return;
      const variantId = row.dataset.lineId || row.dataset.variantId;

      if (action === 'remove') {
        const title = row.querySelector('.custom-quote-page__item-title')?.textContent || 'Item';
        QuoteManager.removeItem(variantId);
        announceToScreenReader(title + ' removed from quote');
      } else if (action === 'decrease' || action === 'increase') {
        // Block qty changes on unavailable/OOS items
        if (row.classList.contains('custom-quote-page__item--unavailable') || row.classList.contains('custom-quote-page__item--out-of-stock')) return;
        const current = parseInt(row.querySelector('.custom-quote-page__qty-value').value, 10);
        const newQty = action === 'increase' ? current + 1 : current - 1;
        QuoteManager.updateQuantity(variantId, newQty);
      } else if (action === 'add-to-cart') {
        // Block add-to-cart on unavailable/OOS items
        if (row.classList.contains('custom-quote-page__item--unavailable') || row.classList.contains('custom-quote-page__item--out-of-stock')) return;
        const qty = parseInt(row.querySelector('.custom-quote-page__qty-value').value, 10) || 1;
        this.addToCart(btn, variantId, qty);
      }
    }

    addToCart(btn, variantId, qty) {
      if (btn.classList.contains('is-loading')) return;

      btn.classList.add('is-loading');
      btn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
      })
      .then(res => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        btn.textContent = 'Added!';

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.textContent = 'Add to Cart';
        }, 2000);
      })
      .catch(() => {
        btn.classList.remove('is-loading');
        btn.textContent = 'Error — retry';
        setTimeout(() => { btn.textContent = 'Add to Cart'; }, 2500);
      });
    }

    handleClear() {
      if (!window.confirm('Remove all items from your quote?')) return;
      QuoteManager.clear();
    }

    addAllToCart(btn) {
      if (btn.classList.contains('is-loading')) return;

      let items = QuoteManager.getItems();
      // Filter out unavailable items if we have validation results
      if (this._validationResults) {
        items = items.filter(i => {
          const r = this._validationResults.get(String(i.variantId));
          return !r || r.status === 'available' || r.status === 'unknown';
        });
      }
      if (!items.length) return;

      btn.classList.add('is-loading');
      btn.textContent = 'Adding all...';

      const payload = items.map(i => ({ id: Number(i.variantId), quantity: i.quantity }));

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload })
      })
      .then(res => {
        if (!res.ok) throw new Error('Add all to cart failed');
        return res.json();
      })
      .then(() => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        btn.textContent = 'All items added!';
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.textContent = 'Add All to Cart';
        }, 2500);
      })
      .catch(() => {
        btn.classList.remove('is-loading');
        btn.textContent = 'Error — retry';
        setTimeout(() => { btn.textContent = 'Add All to Cart'; }, 2500);
      });
    }

    /**
     * Run availability validation on all quote items.
     */
    async runValidation() {
      try {
        this._validationResults = await QuoteValidator.validateAll();
        this.applyValidationUI();
        if (this.handleDataChanges()) {
          // Re-render to show updated prices, then re-apply validation UI
          this.renderItems();
          this.applyValidationUI();
        }
      } catch (e) {
        console.warn('[QuotePage] Validation failed:', e);
      }
    }

    /**
     * Apply validation classes and badges to item rows based on cached results.
     */
    applyValidationUI() {
      if (!this._validationResults) return;
      const rows = this.itemsContainer.querySelectorAll('.custom-quote-page__item');
      let hasIssues = false;

      rows.forEach(row => {
        // Variant-keyed: QuoteValidator builds this Map with String(item.variantId).
        const vid = row.dataset.variantId;
        const result = this._validationResults.get(vid);

        row.classList.remove('custom-quote-page__item--unavailable', 'custom-quote-page__item--out-of-stock');
        const oldBadge = row.querySelector('.custom-quote-page__availability-badge');
        if (oldBadge) oldBadge.remove();
        const oldNotice = row.querySelector('.custom-quote-page__price-notice');
        if (oldNotice) oldNotice.remove();

        if (!result || result.status === 'available' || result.status === 'unknown') {
          if (result && result.priceChanged) {
            const details = row.querySelector('.custom-quote-page__item-details');
            if (details) {
              details.insertAdjacentHTML('beforeend',
                '<span class="custom-quote-page__price-notice">Price updated to $' + parseFloat(result.newPrice).toFixed(2) + '</span>');
            }
          }
          return;
        }

        hasIssues = true;

        if (result.status === 'out_of_stock') {
          row.classList.add('custom-quote-page__item--out-of-stock');
          const details = row.querySelector('.custom-quote-page__item-details');
          if (details) {
            details.insertAdjacentHTML('afterbegin',
              '<span class="custom-quote-page__availability-badge custom-quote-page__availability-badge--oos">Out of Stock</span>');
          }
        } else {
          row.classList.add('custom-quote-page__item--unavailable');
          const details = row.querySelector('.custom-quote-page__item-details');
          if (details) {
            details.insertAdjacentHTML('afterbegin',
              '<span class="custom-quote-page__availability-badge">Unavailable</span>');
          }
        }
      });

      this.toggleUnavailableBanner(hasIssues);
    }

    /**
     * Show or hide the unavailable items warning banner.
     */
    toggleUnavailableBanner(show) {
      let banner = this.querySelector('.custom-quote-page__unavailable-banner');
      if (show && !banner) {
        this.itemsContainer.insertAdjacentHTML('beforebegin',
          '<div class="custom-quote-page__unavailable-banner" role="alert">' +
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm-.75 3.75a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4zM8 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/></svg>' +
          '<span>Some items are no longer available. Remove them to continue.</span></div>');
      } else if (!show && banner) {
        banner.remove();
      }
    }

    /**
     * Handle data changes — update localStorage silently. Returns true if any changed.
     */
    handleDataChanges() {
      if (!this._validationResults) return false;
      let changed = false;
      this._validationResults.forEach((result, vid) => {
        if (result.priceChanged && result.newPrice) {
          QuoteManager.updatePrice(vid, result.newPrice);
          changed = true;
        }
        if (result.metaChanged && result.newMeta) {
          QuoteManager.updateVariantMeta(vid, result.newMeta);
          changed = true;
        }
      });
      return changed;
    }
  }

  customElements.define('custom-quote-page', CustomQuotePage);
}

/**
 * <custom-quote-form> Web Component
 * Quote submission form with validation, phone formatting, zip lookup,
 * conditional org field, file upload UI, and simulated submit.
 * Phase 4 is UI-only — handleSubmit() simulates success without backend.
 */
if (!customElements.get('custom-quote-form')) {
  class CustomQuoteForm extends HTMLElement {
    connectedCallback() {
      this.formEl = this.querySelector('.custom-quote-form__form');
      this.layout = this.querySelector('.custom-quote-form__layout');
      this.emptyState = this.querySelector('.custom-quote-form__empty');
      this.successState = this.querySelector('.custom-quote-form__success');
      this.sidebarItems = this.querySelector('.custom-quote-form__sidebar-items');
      this.subtotalEl = this.querySelector('.custom-quote-form__sidebar-subtotal');
      this.submitBtn = this.querySelector('.custom-quote-form__submit');
      this.submitText = this.querySelector('.custom-quote-form__submit-text');
      this.submitLoading = this.querySelector('.custom-quote-form__submit-loading');

      // Field references
      this.nameInput = this.querySelector('#quote-form-name');
      this.emailInput = this.querySelector('#quote-form-email');
      this.phoneInput = this.querySelector('#quote-form-phone');
      this.zipInput = this.querySelector('#quote-form-zip');
      this.streetInput = this.querySelector('#quote-form-street');
      this.customerTypeSelect = this.querySelector('#quote-form-customer-type');
      this.orgField = this.querySelector('.custom-quote-form__org-field');
      this.orgInput = this.querySelector('#quote-form-org');
      this.zipStatus = this.querySelector('.custom-quote-form__zip-status');

      // File upload references
      this.dropzone = this.querySelector('.custom-quote-form__dropzone');
      this.fileInput = this.querySelector('.custom-quote-form__file-input');
      this.fileList = this.querySelector('.custom-quote-form__file-list');
      this.files = [];
      this._validationResults = null;

      // Check if quote has items
      const items = QuoteManager.getItems();
      if (items.length === 0) {
        this.emptyState.style.display = '';
        this.layout.style.display = 'none';
        return;
      }

      this.emptyState.style.display = 'none';
      this.layout.style.display = '';

      this.renderSummary();
      this.runValidation();
      this.initPhoneFormatting();
      this.initZipLookup();
      this.initCustomerType();
      this.initFileUpload();
      this.initValidation();
      this.initPlaceholderStyle();
      this.autoPopulateFromPrevious();
    }

    /**
     * Add 'is-placeholder' class to selects when showing placeholder text.
     */
    initPlaceholderStyle() {
      [this.querySelector('#quote-form-timeline'), this.customerTypeSelect].forEach(sel => {
        if (!sel) return;
        if (!sel.value) sel.classList.add('is-placeholder');
        sel.addEventListener('change', () => {
          sel.classList.toggle('is-placeholder', !sel.value);
        });
      });
    }

    /**
     * Auto-populate form fields from a previous submission (for re-submissions).
     */
    autoPopulateFromPrevious() {
      try {
        const raw = localStorage.getItem('custom_quote_submission');
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || !data.submitted) return;

        // Only auto-populate if the quote was modified (re-submission scenario)
        if (!data.modifiedAfterSubmission) return;

        if (data.name) this.nameInput.value = data.name;
        if (data.email) this.emailInput.value = data.email;
        if (data.phone) this.phoneInput.value = data.phone;
        if (data.zip) {
          this.zipInput.value = data.zip;
          // Restore city/state from saved data
          if (data.city && data.state) {
            this.zipCity = data.city;
            this.zipState = data.state;
            this.zipValid = true;
            this.zipStatus.textContent = '\u2713 ' + data.city + ', ' + data.state;
            this.zipStatus.className = 'custom-quote-form__zip-status is-success';
          }
        }

        // Restore street address
        if (data.street && this.streetInput) this.streetInput.value = data.street;

        // Restore customer type + org field
        if (data.customerType) {
          this.customerTypeSelect.value = data.customerType;
          this.customerTypeSelect.classList.remove('is-placeholder');

          if (data.customerType === 'Commercial') {
            this.orgInput.disabled = false;
            this.orgInput.setAttribute('required', '');
            if (data.organization && data.organization !== 'n/a - residence') {
              this.orgInput.value = data.organization;
            }
          } else if (data.customerType === 'Residential') {
            this.orgInput.disabled = true;
            this.orgInput.removeAttribute('required');
            this.orgInput.value = 'n/a - residence';
          }
        }

        // Restore timeline
        const timelineSelect = this.querySelector('#quote-form-timeline');
        if (timelineSelect && data.timeline) {
          timelineSelect.value = data.timeline;
          timelineSelect.classList.remove('is-placeholder');
        }

        // Restore notes
        const notesTextarea = this.querySelector('#quote-form-notes');
        if (notesTextarea && data.notes) notesTextarea.value = data.notes;

        // Change button text to "Resubmit Request"
        if (this.submitText) this.submitText.textContent = 'Resubmit Request';

        this.updateSubmitState();
      } catch (e) { /* silently fail */ }
    }

    /**
     * Render compact quote summary in the sidebar.
     */
    renderSummary() {
      const items = QuoteManager.getItems();
      const subtotal = QuoteManager.getSubtotal();

      const html = items.map(item => {
        const price = parseFloat(String(item.price).replace(/,/g, '')) || 0;
        const lineTotal = price * item.quantity;
        const tag = QuoteManager.getItemTag(item);

        const imgSrc = item.image ? _sizedImage(item.image, '96x') : '';
        const imageHtml = imgSrc
          ? `<img class="custom-quote-form__sidebar-img" src="${imgSrc}" alt="${_escapeHtml(item.title)}" loading="lazy" onerror="this.onerror=null;this.src='${QUOTE_PLACEHOLDER_SVG}';this.style.objectFit='contain';">`
          : `<div class="custom-quote-form__sidebar-img-placeholder"></div>`;

        return `<div class="custom-quote-form__sidebar-item" data-variant-id="${item.variantId}" data-line-id="${item.lineId || item.variantId}">
          ${imageHtml}
          <div class="custom-quote-form__sidebar-item-info">
            <span class="custom-quote-form__sidebar-item-title">${_escapeHtml(item.title)}${tag ? ` <span class="custom-quote-addon-tag${tag.ref ? ' custom-quote-addon-tag--ref' : ''}">${_escapeHtml(tag.text)}</span>` : ''}</span>
            <span class="custom-quote-form__sidebar-item-meta">Qty: ${item.quantity} &middot; ${_formatMoney(lineTotal)}</span>
            ${item.note ? `<span class="custom-quote-form__sidebar-item-note">${_escapeHtml(item.note)}</span>` : ''}
          </div>
        </div>`;
      }).join('');

      this.sidebarItems.innerHTML = html;
      this.subtotalEl.innerHTML = _formatMoney(subtotal);
    }

    /**
     * Phone number auto-formatting — handles +1, leading-1, standard 10-digit.
     * Copied from contact-form.liquid.
     */
    initPhoneFormatting() {
      this.phoneInput.addEventListener('input', (e) => {
        let value = e.target.value.replace(/[^\d+]/g, '');

        if (value.charAt(0) === '+') {
          const digits = value.substring(1).replace(/\D/g, '');
          if (digits.length === 0) {
            value = '+';
          } else if (digits.charAt(0) === '1') {
            if (digits.length <= 1) value = '+' + digits;
            else if (digits.length <= 4) value = '+1 (' + digits.substring(1);
            else if (digits.length <= 7) value = '+1 (' + digits.substring(1, 4) + ') ' + digits.substring(4);
            else value = '+1 (' + digits.substring(1, 4) + ') ' + digits.substring(4, 7) + '-' + digits.substring(7, 11);
          } else {
            value = '+' + digits;
          }
        } else if (value.charAt(0) === '1' && value.length > 1) {
          if (value.length <= 1) value = '1';
          else if (value.length <= 4) value = '1 (' + value.substring(1);
          else if (value.length <= 7) value = '1 (' + value.substring(1, 4) + ') ' + value.substring(4);
          else value = '1 (' + value.substring(1, 4) + ') ' + value.substring(4, 7) + '-' + value.substring(7, 11);
        } else if (value.length > 0) {
          if (value.length <= 3) value = '(' + value;
          else if (value.length <= 6) value = '(' + value.substring(0, 3) + ') ' + value.substring(3);
          else value = '(' + value.substring(0, 3) + ') ' + value.substring(3, 6) + '-' + value.substring(6, 10);
        }

        e.target.value = value;
        this.updateSubmitState();
      });
    }

    /**
     * Zip code lookup via zippopotam.us API with 500ms debounce.
     * Stores resolved city/state for use in submission data and address block.
     */
    initZipLookup() {
      this.zipCity = '';
      this.zipState = '';
      this.zipValid = false;

      let timer;
      this.zipInput.addEventListener('input', () => {
        // Enforce 5-digit limit
        let zip = this.zipInput.value.replace(/\D/g, '');
        if (zip.length > 5) zip = zip.substring(0, 5);
        this.zipInput.value = zip;

        clearTimeout(timer);

        if (zip.length < 5) {
          this.zipStatus.textContent = '';
          this.zipStatus.className = 'custom-quote-form__zip-status';
          this.zipCity = '';
          this.zipState = '';
          this.zipValid = false;
          this.updateSubmitState();
          return;
        }

        // Reset validity while looking up
        this.zipValid = false;
        this.updateSubmitState();

        timer = setTimeout(() => {
          this.zipStatus.textContent = 'Looking up...';
          this.zipStatus.className = 'custom-quote-form__zip-status';

          fetch('https://api.zippopotam.us/us/' + zip)
            .then(r => { if (!r.ok) throw new Error(); return r.json(); })
            .then(data => {
              this.zipCity = data.places[0]['place name'];
              this.zipState = data.places[0]['state abbreviation'];
              this.zipValid = true;
              this.zipStatus.textContent = '\u2713 ' + this.zipCity + ', ' + this.zipState;
              this.zipStatus.className = 'custom-quote-form__zip-status is-success';
              this.updateSubmitState();
            })
            .catch(() => {
              this.zipCity = '';
              this.zipState = '';
              this.zipValid = false;
              this.zipStatus.textContent = 'Zip code not found \u2014 please verify';
              this.zipStatus.className = 'custom-quote-form__zip-status is-error';
              this.updateSubmitState();
            });
        }, 500);
      });
    }

    /**
     * Organization Name field — always visible.
     * Default: disabled with placeholder hint until Residential/Commercial is selected.
     * Residential: disabled, value = "n/a - residence"
     * Commercial: enabled, editable, required
     */
    initCustomerType() {
      // Start disabled until a selection is made
      this.orgInput.disabled = true;

      this.customerTypeSelect.addEventListener('change', () => {
        const val = this.customerTypeSelect.value;

        if (val === 'Commercial') {
          this.orgInput.disabled = false;
          this.orgInput.setAttribute('required', '');
          this.orgInput.placeholder = '';
          if (this.orgInput.value === 'n/a - residence') this.orgInput.value = '';
          this.orgInput.focus();
        } else if (val === 'Residential') {
          this.orgInput.disabled = true;
          this.orgInput.removeAttribute('required');
          this.orgInput.value = 'n/a - residence';
          this.orgInput.placeholder = '';
          this.clearError('organization');
        }

        this.updateSubmitState();
      });
    }

    /**
     * File upload drag & drop zone.
     *
     * ⛔ THIS WAS DECORATIVE FOR MONTHS, AND IT LOOKED FINISHED FROM EVERY ANGLE.
     * The zone accepted drops, listed each file with its size, and offered a remove
     * button — and then `this.files` was read by nothing. No upload, no field on the
     * payload: every attachment a customer added went in the bin at submit, while the
     * UI told them it had been attached. (The section comment said "Phase 5 will
     * handle actual upload"; Phase 5 never came, and nothing in the interface said so.)
     *
     * ⚠ THE BACKEND WAS READY THE WHOLE TIME. `index.js` already maps `body.fileUrls`
     * onto `lead.attachments`, and `email/team-email.js` already renders an
     * "Attachments (n)" section from it — the same path `contact-form.liquid` has been
     * feeding correctly since it was built. The only missing piece was the client
     * actually uploading and sending the list. Fixed 2026-08-09 (Ryan: *"need to
     * definitely fix the file upload for ALL website forms"*).
     */
    initFileUpload() {
      // Click to open file picker
      this.dropzone.addEventListener('click', (e) => {
        if (e.target.closest('.custom-quote-form__file-remove')) return;
        this.fileInput.click();
      });
      this.dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.fileInput.click();
        }
      });

      // File input change
      this.fileInput.addEventListener('change', () => {
        this.addFiles(this.fileInput.files);
        this.fileInput.value = '';
      });

      // Drag events
      this.dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.dropzone.classList.add('is-dragover');
      });
      this.dropzone.addEventListener('dragleave', () => {
        this.dropzone.classList.remove('is-dragover');
      });
      this.dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropzone.classList.remove('is-dragover');
        this.addFiles(e.dataTransfer.files);
      });

      // Delegate remove clicks on file list
      this.fileList.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.custom-quote-form__file-remove');
        if (!removeBtn) return;
        const index = parseInt(removeBtn.dataset.index, 10);
        this.files.splice(index, 1);
        this.renderFileList();
      });
    }

    addFiles(fileListObj) {
      // 10 MB is the worker's own ceiling (`MAX_FILE_SIZE` in customer-public.js).
      // Caught here so the customer is told at the moment they pick the file, rather
      // than at submit — when they have already filled the form in.
      const MAX_BYTES = 10 * 1024 * 1024;
      const tooBig = [];
      for (let i = 0; i < fileListObj.length; i++) {
        const f = fileListObj[i];
        if (f.size > MAX_BYTES) { tooBig.push(f.name); continue; }
        this.files.push(f);
      }
      if (tooBig.length) {
        this.showFileError(tooBig.join(', ') + (tooBig.length > 1 ? ' are' : ' is') + ' over 10 MB. Email large files to sales@blueskyfitsupply.com.');
      } else {
        this.clearFileError();
      }
      this.renderFileList();
    }

    /** Inline message under the file list. Created lazily — the markup has no slot. */
    showFileError(message) {
      if (!this._fileError) {
        this._fileError = document.createElement('p');
        this._fileError.className = 'custom-quote-form__file-error';
        this._fileError.setAttribute('role', 'status');
        this._fileError.style.cssText = 'margin:6px 0 0;font-size:13px;color:#DC2626;';
        this.fileList.parentNode.insertBefore(this._fileError, this.fileList.nextSibling);
      }
      this._fileError.textContent = message;
      this._fileError.style.display = '';
    }

    clearFileError() {
      if (this._fileError) this._fileError.style.display = 'none';
    }

    /**
     * Upload every staged file and return `[{name, url}]` for the payload.
     *
     * ⚠ A FAILED UPLOAD MUST NEVER COST THE LEAD. This is a finished, priced-out quote
     * request with a cart behind it; refusing to submit because an attachment would not
     * go up trades a customer for a file. So failures are COUNTED and reported back to
     * the caller, which appends a line to the notes — the rep then knows to ask, which
     * is the one outcome worse than neither: silently losing the file.
     * (`contact-form.liquid` blocks instead. It can afford to: there is no cart there.)
     */
    async uploadFiles() {
      const uploaded = [];
      const failed = [];
      for (const file of this.files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/upload', { method: 'POST', body: fd });
          const data = await res.json();
          if (!res.ok || !data.success || !data.url) throw new Error(data.error || 'upload failed');
          // Relative URL kept as returned: index.js and team-email.js both prepend the
          // worker origin themselves, and an absolute one here doubles the host.
          uploaded.push({ name: data.name || file.name, url: data.url });
        } catch (e) {
          failed.push(file.name);
        }
      }
      return { uploaded: uploaded, failed: failed };
    }

    renderFileList() {
      if (this.files.length === 0) {
        this.fileList.innerHTML = '';
        return;
      }

      const html = this.files.map((file, i) => {
        const size = file.size < 1024 * 1024
          ? (file.size / 1024).toFixed(0) + ' KB'
          : (file.size / (1024 * 1024)).toFixed(1) + ' MB';

        return `<div class="custom-quote-form__file-entry">
          <span class="custom-quote-form__file-name">${_escapeHtml(file.name)}</span>
          <span class="custom-quote-form__file-size">${size}</span>
          <button type="button" class="custom-quote-form__file-remove" data-index="${i}" aria-label="Remove ${_escapeHtml(file.name)}">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>
          </button>
        </div>`;
      }).join('');

      this.fileList.innerHTML = html;
    }

    /**
     * Initialize validation — real-time submit state, blur errors, form submit.
     */
    initValidation() {
      const requiredInputs = [this.nameInput, this.emailInput, this.phoneInput, this.zipInput];

      // Update submit state on every input/change
      requiredInputs.forEach(input => {
        input.addEventListener('input', () => this.updateSubmitState());
        input.addEventListener('blur', () => this.validateField(input));
      });

      this.customerTypeSelect.addEventListener('change', () => this.updateSubmitState());
      this.customerTypeSelect.addEventListener('blur', () => this.validateField(this.customerTypeSelect));

      this.orgInput.addEventListener('input', () => this.updateSubmitState());
      this.orgInput.addEventListener('blur', () => {
        if (this.orgInput.hasAttribute('required')) this.validateField(this.orgInput);
      });

      // Form submit
      this.formEl.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }

    /**
     * Check if all required fields are valid and enable/disable submit.
     */
    updateSubmitState() {
      const valid = this.isFormValid();
      this.submitBtn.disabled = !valid;
    }

    isFormValid() {
      if (!this.nameInput.value.trim()) return false;
      if (!this.emailInput.value.trim() || !this.isValidEmail(this.emailInput.value)) return false;

      // Phone: strip formatting, need at least 10 digits
      const phoneDigits = this.phoneInput.value.replace(/\D/g, '');
      if (phoneDigits.length < 10) return false;

      // Zip: need at least 5 digits AND must be validated by API
      const zipDigits = this.zipInput.value.replace(/\D/g, '');
      if (zipDigits.length < 5) return false;
      if (!this.zipValid) return false;

      if (!this.customerTypeSelect.value) return false;

      // Org name required when Commercial (skip when disabled/Residential)
      if (this.customerTypeSelect.value === 'Commercial' && !this.orgInput.disabled && !this.orgInput.value.trim()) return false;

      return true;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    }

    /**
     * Validate a single field and show/clear error message.
     */
    validateField(input) {
      const name = input.name;
      let error = '';

      if (name === 'name' && !input.value.trim()) {
        error = 'Name is required';
      } else if (name === 'email') {
        if (!input.value.trim()) error = 'Email is required';
        else if (!this.isValidEmail(input.value)) error = 'Please enter a valid email address';
      } else if (name === 'phone') {
        const digits = input.value.replace(/\D/g, '');
        if (digits.length < 10) error = 'Please enter a valid phone number';
      } else if (name === 'zip') {
        const digits = input.value.replace(/\D/g, '');
        if (digits.length < 5) error = 'Please enter a valid zip code';
        else if (!this.zipValid) error = 'Zip code not recognized — please verify';
      } else if (name === 'customer_type') {
        if (!input.value) error = 'Please select one';
      } else if (name === 'organization') {
        if (input.hasAttribute('required') && !input.value.trim()) error = 'Organization name is required';
      }

      if (error) {
        this.showError(name, error);
        input.classList.add('has-error');
      } else {
        this.clearError(name);
        input.classList.remove('has-error');
      }

      return !error;
    }

    showError(fieldName, message) {
      const el = this.querySelector(`.custom-quote-form__error[data-for="${fieldName}"]`);
      if (el) el.textContent = message;
    }

    clearError(fieldName) {
      const el = this.querySelector(`.custom-quote-form__error[data-for="${fieldName}"]`);
      if (el) el.textContent = '';
      const input = this.querySelector(`[name="${fieldName}"]`);
      if (input) input.classList.remove('has-error');
    }

    /**
     * Validate all fields and focus the first invalid one.
     * Returns true if all valid.
     */
    validateAll() {
      const fields = [
        this.nameInput,
        this.emailInput,
        this.phoneInput,
        this.zipInput,
        this.customerTypeSelect
      ];

      if (this.customerTypeSelect.value === 'Commercial') {
        fields.push(this.orgInput);
      }

      let firstInvalid = null;
      fields.forEach(field => {
        const valid = this.validateField(field);
        if (!valid && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstInvalid.focus({ preventScroll: true }), 400);
        return false;
      }

      return true;
    }

    /**
     * Handle form submission.
     * Phase 4: UI-only — simulates success after 1500ms.
     * Does NOT clear localStorage (Phase 5 will do that after real Draft Order creation).
     */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-16, AIOPS 468b6f2dfc41) — the submitting state,
       in one place, because three copies of it had to agree and the first one was in the wrong
       position. `is-loading` is what the re-entry guard reads, so putting the class on IS the
       lock; the rest is what the customer sees. */
    _enterSubmitting() {
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-busy', 'true');
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitText.style.display = 'none';
      this.submitLoading.style.display = '';
    }

    _exitSubmitting() {
      this.submitBtn.classList.remove('is-loading');
      this.submitBtn.disabled = false;
      this.submitBtn.removeAttribute('aria-busy');
      this.submitBtn.removeAttribute('aria-disabled');
      this.submitText.style.display = '';
      this.submitLoading.style.display = 'none';
    }

    async handleSubmit() {
      if (!this.validateAll()) return;
      if (this.submitBtn.classList.contains('is-loading')) return;

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-16, AIOPS 468b6f2dfc41) — ⛔ THE LOCK GOES ON
         BEFORE THE FIRST AWAIT, NOT AFTER IT. The guard above keys on `is-loading`, and the fresh
         availability check below is a network call that asks about every line on the quote. While
         it ran, the button was still enabled, still read "Submit", and still passed that guard —
         so a customer who clicked again because nothing had visibly happened started a SECOND
         submission, and the guard they were being protected by had not been armed yet.
         RW-1413-A / RW-1414-A / RW-1415-A (2026-09-16) were one order that arrived three times,
         25s and 95s apart.
         ⚠️ THE SERVER IS WHAT GUARANTEES ONE DEAL — a page reload or a second tab defeats any
         button, and rfq-worker now absorbs an identical re-submission into the deal it already
         made. This removes the likeliest way to ask for it twice; it is not the safety net. */
      this._enterSubmitting();

      // Fresh availability check before submitting
      try {
        const freshResults = await QuoteValidator.validateAll();
        if (QuoteValidator.hasUnavailable(freshResults)) {
          this._validationResults = freshResults;
          this.applyValidationUI();
          /* ⛔ AND IT COMES BACK OFF ON THE REFUSAL. The customer is being asked to change the
             quote; a button left spinning would tell them the site had hung. */
          this._exitSubmitting();
          return;
        }
      } catch (e) {
        // Network error — proceed anyway (safe default)
      }

      // Attachments go up BEFORE the quote POST, on their own calls, so the one
      // request that carries the lead stays small and JSON. Same ordering the Weight
      // Designer uses for its artwork, and for the same reason.
      let fileUrls = [];
      let failedUploads = [];
      if (this.files.length) {
        // No separate "uploading" copy: the button is already in its loading state
        // (submitText is display:none by this point, so writing to it says nothing).
        const result = await this.uploadFiles();
        fileUrls = result.uploaded;
        failedUploads = result.failed;
      }

      // Build request payload
      const quoteItems = QuoteManager.getItems();
      const currentItems = quoteItems.map(i => ({ variantId: i.variantId, title: i.title, quantity: i.quantity }));

      // Check for previous submission (revision tracking)
      let baseQuoteNumber, baseQuoteRef, revisionIndex, history;
      try {
        const prev = JSON.parse(localStorage.getItem('custom_quote_submission') || 'null');
        if (prev && prev.submitted && prev.baseQuoteNumber) {
          baseQuoteNumber = prev.baseQuoteNumber;
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — send the PREVIOUS REF, not just
             its number. Without it the worker had nothing tying this revision to the deal it
             revises, so it re-ran the whole capacity cascade and could hand the revision to a
             different rep than the one already working the customer (RW-1394-A revised into
             JW-1394-B). The worker verifies this ref server-side before trusting it. */
          baseQuoteRef = prev.refNumber || null;
          revisionIndex = (prev.revisionIndex || 0) + 1;
          history = Array.isArray(prev.history) ? prev.history.slice() : [];
          history.push({ ref: prev.refNumber, submittedAt: prev.submittedAt, itemCount: prev.countAtSubmission, items: prev.itemsSnapshot || [] });
        } else {
          baseQuoteNumber = null;
          baseQuoteRef = null;
          revisionIndex = 0;
          history = [];
        }
      } catch (e) {
        baseQuoteNumber = null;
        baseQuoteRef = null;
        revisionIndex = 0;
        history = [];
      }

      // Determine addon status for revision submissions
      let prevSubmittedAt = null;
      if (revisionIndex > 0) {
        try {
          const prev = JSON.parse(localStorage.getItem('custom_quote_submission') || 'null');
          if (prev && prev.submittedAt) prevSubmittedAt = prev.submittedAt;
        } catch (e) { /* ignore */ }
      }

      const payload = {
        items: quoteItems.map(i => ({
          variantId: i.variantId,
          quantity: i.quantity,
          title: i.title,
          // Variant metadata for the Workstation's subtext rendering — without
          // these the rep sees no color/size/option detail when they open the
          // RFQ. optionValues is an array of {name, value} pairs.
          variantTitle: i.variantTitle || '',
          optionValues: Array.isArray(i.optionValues) ? i.optionValues : [],
          // Calculator-derived metadata (Room Dimensions, Roll Layout, etc.).
          // Worker appends these to the item's subDescriptions so the rep
          // sees "Room Dims: 14' × 16'" inline without having to open notes.
          properties: (i.properties && typeof i.properties === 'object') ? i.properties : {},
          note: i.note || '',
          url: i.url || '',
          image: i.image || '',
          isAddon: !!(prevSubmittedAt && i.addedAt && i.addedAt > prevSubmittedAt)
        })),
        customer: {
          name: this.nameInput.value.trim(),
          email: this.emailInput.value.trim(),
          phone: this.phoneInput.value.trim(),
          zip: this.zipInput.value.trim(),
          street: this.streetInput ? this.streetInput.value.trim() : '',
          city: this.zipCity || '',
          state: this.zipState || '',
          customerType: this.customerTypeSelect.value,
          organization: this.orgInput.value.trim(),
          timeline: (this.querySelector('#quote-form-timeline') || {}).value || ''
        },
        /* ⚠ A FAILED UPLOAD IS WRITTEN INTO THE NOTES, not swallowed. The rep is the
           only one who can recover it (by asking the customer), and they can only do
           that if the lead says a file was meant to be here. */
        notes: (function (base) {
          return failedUploads.length
            ? (base ? base + '\n\n' : '') + '⚠ The customer attached ' + failedUploads.length + ' file(s) that failed to upload: ' + failedUploads.join(', ') + '. Ask them to email these to you.'
            : base;
        })(((this.querySelector('#quote-form-notes') || {}).value || '').trim()),
        // The worker maps this onto `lead.attachments`; team-email.js renders it as
        // the "Attachments (n)" block. Relative URLs — both prepend the origin.
        fileUrls: fileUrls,
        revisionIndex: revisionIndex,
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE — site-wide UTM journey attribution so
           Add-to-Quote/RFQ leads get credited to the ad that drove the visit
           instead of defaulting to Organic. Read from the shared tracker
           (utm-journey-tracker.js window.BSFSJourney); guarded in case it
           hasn't loaded. sourceUrl stays the submit page (journey carries the
           real entry/first touch). */
        journey:    (window.BSFSJourney && window.BSFSJourney.read())       || [],
        utm:        (window.BSFSJourney && window.BSFSJourney.utmSummary()) || {},
        gaClientId: (window.BSFSJourney && window.BSFSJourney.gaClientId()) || null,
        sourceUrl:  window.location.href,
        referrer:   document.referrer || ''
      };

      // Include baseQuoteNumber only for revisions (omit on first submission so worker generates it)
      if (baseQuoteNumber) {
        payload.baseQuoteNumber = baseQuoteNumber;
        // Pairs with it: the ref the number came from, so the worker can pin this revision to
        // that deal's owner instead of re-routing it. `refNumber` has been stored alongside
        // `baseQuoteNumber` since revision tracking was written, so submissions already sitting
        // in a visitor's localStorage carry one and need no migration; the guard is for a
        // partial record, and its absence simply falls back to the old cascade behaviour.
        if (baseQuoteRef) payload.baseQuoteRef = baseQuoteRef;
      }

      try {
        const res = await fetch('https://rfq-worker.ryan-29b.workers.dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.error || data.detail || 'Quote submission failed');
        }

        // Success — save submission data for review page and auto-population
        const refNum = data.quoteRef;
        try {
          localStorage.setItem('custom_quote_submission', JSON.stringify({
            submitted: true,
            refNumber: refNum,
            baseQuoteNumber: data.baseQuoteNumber,
            revisionIndex: data.revisionIndex,
            draftOrderId: data.draftOrderId,
            draftOrderName: data.draftOrderName,
            submittedAt: new Date().toISOString(),
            countAtSubmission: QuoteManager.getItemCount(),
            itemsSnapshot: currentItems,
            history: history,
            name: this.nameInput.value.trim(),
            email: this.emailInput.value.trim(),
            phone: this.phoneInput.value.trim(),
            zip: this.zipInput.value.trim(),
            street: this.streetInput ? this.streetInput.value.trim() : '',
            city: this.zipCity,
            state: this.zipState,
            customerType: this.customerTypeSelect.value,
            organization: this.orgInput.value.trim(),
            timeline: (this.querySelector('#quote-form-timeline') || {}).value || '',
            notes: (this.querySelector('#quote-form-notes') || {}).value || ''
          }));
        } catch (e) { /* silently fail */ }

        // Show success state
        this.layout.style.display = 'none';
        this.successState.style.display = '';
        announceToScreenReader('Quote submitted successfully');

        const refEl = this.querySelector('.custom-quote-form__success-ref');
        if (refEl) refEl.textContent = 'Reference: ' + refNum;

        // Immediately update header badge to green checkmark
        const drawerEl = document.querySelector('quote-drawer');
        if (drawerEl && drawerEl.updateBadge) {
          drawerEl.updateBadge();
        } else {
          const bubble = document.getElementById('quote-icon-bubble');
          if (bubble) {
            bubble.innerHTML = '<div class="custom-quote-badge custom-quote-badge--submitted absolute text-center"><span aria-hidden="true">\u2713</span></div>';
          }
        }

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {
        // Error — show user-friendly message, do NOT clear quote data
        this._exitSubmitting();

        // Show error message
        let errorEl = this.querySelector('.custom-quote-form__error');
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.className = 'custom-quote-form__error';
          errorEl.style.cssText = 'color:#c00;background:#fff0f0;border:1px solid #c00;padding:12px 16px;border-radius:6px;margin-bottom:16px;font-size:14px;';
          this.submitBtn.parentElement.insertBefore(errorEl, this.submitBtn);
        }
        errorEl.textContent = 'Something went wrong submitting your request. Please try again, or contact us directly at info@blueskyfitsupply.com.';
        errorEl.style.display = '';

        console.error('RFQ submit error:', err.message);
      }
    }

    /**
     * Run availability validation on quote items.
     */
    async runValidation() {
      try {
        this._validationResults = await QuoteValidator.validateAll();
        if (this.handleDataChanges()) {
          this.renderSummary();
        }
        this.applyValidationUI();
      } catch (e) {
        console.warn('[QuoteForm] Validation failed:', e);
      }
    }

    /**
     * Handle data changes — update localStorage silently. Returns true if any changed.
     */
    handleDataChanges() {
      if (!this._validationResults) return false;
      let changed = false;
      this._validationResults.forEach((result, vid) => {
        if (result.priceChanged && result.newPrice) {
          QuoteManager.updatePrice(vid, result.newPrice);
          changed = true;
        }
        if (result.metaChanged && result.newMeta) {
          QuoteManager.updateVariantMeta(vid, result.newMeta);
          changed = true;
        }
      });
      return changed;
    }

    /**
     * Apply validation UI to sidebar items and submit button.
     */
    applyValidationUI() {
      if (!this._validationResults) return;
      const sidebarItems = this.sidebarItems.querySelectorAll('.custom-quote-form__sidebar-item');
      let hasIssues = false;

      sidebarItems.forEach(item => {
        // Variant-keyed: QuoteValidator builds this Map with String(item.variantId).
        const vid = item.dataset.variantId;
        const result = this._validationResults.get(vid);

        item.classList.remove('custom-quote-form__sidebar-item--unavailable');

        if (result && (result.status === 'deleted' || result.status === 'variant_removed' || result.status === 'out_of_stock')) {
          item.classList.add('custom-quote-form__sidebar-item--unavailable');
          hasIssues = true;
        }
      });

      // Show/hide warning above submit button
      let warning = this.querySelector('.custom-quote-form__unavailable-warning');
      if (hasIssues && !warning) {
        this.submitBtn.insertAdjacentHTML('beforebegin',
          '<div class="custom-quote-form__unavailable-warning" role="alert">' +
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm-.75 3.75a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4zM8 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/></svg>' +
          '<span>Some items are unavailable. <a href="/pages/request-a-quote">Review your quote</a> and remove them before submitting.</span></div>');
        this.submitBtn.disabled = true;
      } else if (!hasIssues && warning) {
        warning.remove();
        this.updateSubmitState();
      }
    }
  }

  customElements.define('custom-quote-form', CustomQuoteForm);
}

/**
 * <custom-quote-status> Web Component
 * Quote status lookup page — customers enter ref + email to check status.
 * Auto-populates from localStorage if a recent submission exists.
 */
if (!customElements.get('custom-quote-status')) {
  class CustomQuoteStatus extends HTMLElement {
    connectedCallback() {
      this.workerUrl = this.dataset.workerUrl;
      this.lookupCard = this.querySelector('.custom-quote-status__lookup-card');
      this.errorState = this.querySelector('.custom-quote-status__error');
      this.results = this.querySelector('.custom-quote-status__results');
      this.formEl = this.querySelector('.custom-quote-status__form');
      this.refInput = this.querySelector('#status-ref');
      this.emailInput = this.querySelector('#status-email');
      this.submitBtn = this.querySelector('.custom-quote-status__submit');
      this.submitText = this.querySelector('.custom-quote-status__submit-text');
      this.submitLoading = this.querySelector('.custom-quote-status__submit-loading');

      // Results elements
      this.banner = this.querySelector('.custom-quote-status__banner');
      this.bannerLabel = this.querySelector('.custom-quote-status__banner-label');
      this.bannerRef = this.querySelector('.custom-quote-status__banner-ref');
      this.bannerMessage = this.querySelector('.custom-quote-status__banner-message');
      this.bannerIcon = this.querySelector('.custom-quote-status__banner-icon');
      this.stepsContainer = this.querySelector('.custom-quote-status__steps');
      this.expiryNotice = this.querySelector('.custom-quote-status__expiry');
      this.expiryText = this.querySelector('.custom-quote-status__expiry-text');
      this.itemsList = this.querySelector('.custom-quote-status__items-list');
      this.checkoutBtn = this.querySelector('.custom-quote-status__checkout-btn');

      // PDF button
      this.pdfBtn = this.querySelector('.custom-quote-status__pdf-btn');
      this.pdfBtnText = this.querySelector('.custom-quote-status__pdf-btn-text');
      this.pdfBtnLoading = this.querySelector('.custom-quote-status__pdf-btn-loading');

      // Bind events
      this.formEl.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleLookup();
      });

      this.querySelector('.custom-quote-status__retry').addEventListener('click', () => this.resetForm());
      this.querySelector('.custom-quote-status__another-btn').addEventListener('click', () => this.resetForm());
      this.pdfBtn.addEventListener('click', () => this.handlePdfDownload());

      this.autoPopulateFromStorage();
    }

    autoPopulateFromStorage() {
      try {
        const raw = localStorage.getItem('custom_quote_submission');
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data || !data.submitted || !data.refNumber || !data.email) return;

        this.refInput.value = data.refNumber;
        this.emailInput.value = data.email;
      } catch (e) { /* silently fail */ }
    }

    async handleLookup() {
      const ref = this.refInput.value.trim();
      const email = this.emailInput.value.trim();

      if (!ref || !email) return;

      // Loading state
      this.submitBtn.disabled = true;
      this.submitText.style.display = 'none';
      this.submitLoading.style.display = '';

      try {
        const url = `${this.workerUrl}/status?ref=${encodeURIComponent(ref)}&email=${encodeURIComponent(email)}`;
        const res = await fetch(url);
        const data = await res.json();

        if (!res.ok || !data.success) {
          this.showErrorState();
          return;
        }

        this.displayResults(data.quote);
      } catch (err) {
        console.error('Status lookup error:', err);
        this.showErrorState();
      } finally {
        this.submitBtn.disabled = false;
        this.submitText.style.display = '';
        this.submitLoading.style.display = 'none';
      }
    }

    displayResults(quote) {
      this.lookupCard.style.display = 'none';
      this.errorState.style.display = 'none';
      this.results.style.display = '';

      // Banner
      const bannerInner = this.querySelector('.custom-quote-status__banner-inner');
      bannerInner.className = 'custom-quote-status__banner-inner custom-quote-status__banner--' + quote.status;
      this.bannerLabel.textContent = quote.statusLabel;
      this.bannerRef.textContent = quote.reference;
      this.bannerMessage.textContent = quote.message;

      // Banner icon
      const iconSvgs = {
        submitted: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
        under_review: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
        quote_sent: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
        accepted: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
        expired: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
      };
      this.bannerIcon.innerHTML = iconSvgs[quote.status] || iconSvgs.submitted;

      // Step progress
      this.deriveStepProgress(quote.status);

      // Expiration notice
      if (quote.expiresAt && quote.status !== 'expired' && quote.status !== 'accepted') {
        this.expiryNotice.style.display = '';
        const expDate = new Date(quote.expiresAt + 'T23:59:59Z');
        const now = new Date();
        const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));

        if (daysLeft <= 3) {
          this.expiryNotice.classList.add('custom-quote-status__expiry--urgent');
          this.expiryText.textContent = daysLeft <= 0 ? 'Expires today' : `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} (${this.formatDate(quote.expiresAt)})`;
        } else {
          this.expiryNotice.classList.remove('custom-quote-status__expiry--urgent');
          this.expiryText.textContent = `Valid until ${this.formatDate(quote.expiresAt)}`;
        }
      } else {
        this.expiryNotice.style.display = 'none';
      }

      // Line items
      if (quote.lineItems && quote.lineItems.length) {
        this.itemsList.innerHTML = quote.lineItems.map(item => {
          const price = parseFloat(item.price) || 0;
          const lineTotal = price * item.quantity;
          const imgHtml = item.image
            ? `<img class="custom-quote-status__item-img" src="${_escapeHtml(item.image)}" alt="${_escapeHtml(item.title)}" loading="lazy" onerror="this.onerror=null;this.src='${QUOTE_PLACEHOLDER_SVG}';this.style.objectFit='contain';">`
            : '<div class="custom-quote-status__item-img custom-quote-status__item-img--placeholder"></div>';
          const titleText = _escapeHtml(item.title);
          const titleHtml = item.handle
            ? `<a href="/products/${_escapeHtml(item.handle)}" target="_blank" rel="noopener" class="custom-quote-status__item-title">${titleText}</a>`
            : `<span class="custom-quote-status__item-title">${titleText}</span>`;
          return `<div class="custom-quote-status__item">
            ${imgHtml}
            <div class="custom-quote-status__item-info">
              ${titleHtml}
              <span class="custom-quote-status__item-meta">Qty: ${item.quantity}${price > 0 ? ' &middot; ' + this.formatMoney(price) + ' each' : ''}</span>
            </div>
            ${price > 0 ? '<strong class="custom-quote-status__item-total">' + this.formatMoney(lineTotal) + '</strong>' : ''}
          </div>`;
        }).join('');
      } else {
        this.itemsList.innerHTML = '<p class="text-sm" style="opacity:0.6;">No line items available.</p>';
      }

      // Checkout button
      if (quote.status === 'quote_sent' && quote.invoiceUrl) {
        this.checkoutBtn.href = quote.invoiceUrl;
        this.checkoutBtn.style.display = '';
      } else {
        this.checkoutBtn.style.display = 'none';
      }

      // PDF download button — show for all statuses except expired
      if (quote.status !== 'expired') {
        this.pdfBtn.style.display = '';
        this._currentRef = this.refInput.value.trim();
        this._currentEmail = this.emailInput.value.trim();
      } else {
        this.pdfBtn.style.display = 'none';
      }

      // Hide steps for expired quotes
      this.stepsContainer.style.display = quote.status === 'expired' ? 'none' : '';

    }

    async handlePdfDownload() {
      if (!this._currentRef || !this._currentEmail) return;

      this.pdfBtn.disabled = true;
      this.pdfBtnText.style.display = 'none';
      this.pdfBtnLoading.style.display = '';

      try {
        const url = `${this.workerUrl}/pdf?ref=${encodeURIComponent(this._currentRef)}&email=${encodeURIComponent(this._currentEmail)}`;
        const res = await fetch(url);

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to generate PDF');
        }

        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${this._currentRef}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('PDF download error:', err);
        alert('Unable to download the PDF. Please try again or contact us.');
      } finally {
        this.pdfBtn.disabled = false;
        this.pdfBtnText.style.display = '';
        this.pdfBtnLoading.style.display = 'none';
      }
    }

    deriveStepProgress(status) {
      const order = ['submitted', 'under_review', 'quote_sent', 'accepted'];
      const currentIndex = order.indexOf(status);

      // Reset all steps
      this.stepsContainer.querySelectorAll('.custom-quote-status__step').forEach(step => {
        step.classList.remove('is-completed', 'is-active');
      });
      this.stepsContainer.querySelectorAll('.custom-quote-status__step-line').forEach(line => {
        line.classList.remove('is-completed');
      });

      if (currentIndex === -1) return; // expired or unknown

      order.forEach((stepName, i) => {
        const stepEl = this.stepsContainer.querySelector(`[data-step="${stepName}"]`);
        if (!stepEl) return;

        if (i < currentIndex) {
          stepEl.classList.add('is-completed');
        } else if (i === currentIndex) {
          stepEl.classList.add('is-active');
        }
      });

      // Fill lines up to current step
      const lines = this.stepsContainer.querySelectorAll('.custom-quote-status__step-line');
      lines.forEach((line, i) => {
        if (i < currentIndex) {
          line.classList.add('is-completed');
        }
      });
    }

    showErrorState() {
      this.lookupCard.style.display = 'none';
      this.results.style.display = 'none';
      this.errorState.style.display = '';
    }

    resetForm() {
      this.lookupCard.style.display = '';
      this.results.style.display = 'none';
      this.errorState.style.display = 'none';
      this.refInput.focus();
    }

    formatDate(dateStr) {
      const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }

    formatMoney(amount) {
      return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

  }

  customElements.define('custom-quote-status', CustomQuoteStatus);
}



/**
 * Initialize the quote badge on page load.
 * Runs immediately to show the badge count without waiting for the drawer to connect.
 */
(function initQuoteBadge() {
  function updateBadge() {
    const bubble = document.getElementById('quote-icon-bubble');
    if (!bubble) return;
    const count = QuoteManager.getItemCount();
    if (count > 0) {
      bubble.innerHTML = `<div class="custom-quote-badge absolute bg-theme-text text-theme-bg text-center">
        <span aria-hidden="true">${count}</span>
        <span class="visually-hidden">${count} item${count !== 1 ? 's' : ''} in quote</span>
      </div>`;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateBadge);
  } else {
    updateBadge();
  }
})();

/* CUSTOM_RFQ_BY_CLAUDE — Genie close effect for cart drawer */
function patchCartDrawerGenie() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;
  cartDrawer.close = function (callback) {
    genieClose(this, document.getElementById('cart-icon'), callback);
  };
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', patchCartDrawerGenie);
} else {
  patchCartDrawerGenie();
}
/* END CUSTOM_RFQ_BY_CLAUDE */
